import { amorphicStatic, property, remote, supertypeClass } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import _ from 'lodash';
import PubSub from 'pubsub-js';
import { Customer } from './Customer';
import { Application, ApplicationSourceType } from './Application';
import { Applicant } from './Applicant';
import { Beneficiary } from './Beneficiary';
import { EnumVal } from '../interfaces/CustomEnum';
import { PolicyAgent } from './PolicyAgent';
import { Assumptions, DocTypes } from '../static/Assumptions';
import { Product, RiderType } from '../static/Product';
import { Party } from './Party';
import { PolicyAccountInfo } from '../account/PolicyAccountInfo';
import { PremiumBreakdown, TLICPremiumBreakdown } from './PremiumBreakdown';
import { PolicyDoc } from './PolicyDoc';
import { WorkflowStateHistory } from './WorkflowStateHistory';
import { FollowUp } from './FollowUp';
import { PackageQuery } from '../docusign/PackageQuery';
import { Person } from './Person';
import { Address } from './Address';
import { Phone } from './Phone';
import { Utils } from '../Utils';
import { ApplicationStateLogic } from '../ApplicationStateLogic';
import { Entity } from './Entity';
import { UniversalBeneficiary } from './UniversalBeneficiary';
import { RateClasses } from '../static/RateClasses';
import { Digit } from './Digit';
import { Quotes } from '../needs/Quotes';
import { ProductWaiverPremiums } from '../static/ProductWaiverPremiums';
import { MetadataHierarchy } from '../metadata/MetadataHierarchy';
import { PolicyChangeHistory } from './PolicyChangeHistory';
import { PolicyChangeHistoryManager } from '../manager/PolicyChangeHistoryManager';
import { PropertyChangedEventNames } from '../registries/PropertyChangedEventNames';
import { AvailableStateRegistry } from '../registries/AvailableStateRegistry';
import { supplementDetails } from '../static/AdditionalSupplementDetails';
import { SubjectManager } from '../subjectManager/SubjectManager';
import { FlatExtra } from './FlatExtra';

import {
  FlatExtraResponse,
  PremiumRequest,
  PremiumResponse,
  ProductServiceClient,
  Rider,
} from '../productService/ProductServiceClient';
import { UwFlatExtra } from '../../../common/js/interfaces/UwFlatExtra';
import { CommonUtils } from '../static/CommonUtils';
import { HitAppCtx, HitAppExecutionCtx } from '../orca/HitAppCtx';
import { WorkflowNames } from '../orca/WorkflowName';
import { ESignDocMetadata, ESignDocMetadataType } from './ESignDocMetadata';
import { ProductApiTypes } from '@hit/policy-admin-types';
import { PremiumService, PremiumServiceResponse } from '../services/PremiumService';
import { Policy as GQLPolicy } from '@hit/hit-api-client';
import { PolicyAmendmentProperty } from '../static/PolicyAmendmentProperty';
import { PremiumLoan } from './PremiumLoan';
import { CollateralAssignment } from './CollateralAssignment';
import { PolicyBusinessPurpose } from './PolicyBusinessPurpose';
import { PolicyRider } from './PolicyRider';
import { IllustrationRequestProperty } from './IllustrationRequestProperty';
import { POLICY_CHANGE_EDITOR, POLICY_CHANGE_HISTORY_STATUS } from '../static/PolicyChangeHistoryStatus';
import { UwReasons } from './UwReasons';
import { Location } from '../../../common/js/interfaces/Location';
import { PayerTypeOptions } from '../interfaces/CommonTypes';
import { serverOnly } from '../helpers';
import { POLICY_TYPES } from '../constants/policy';
import { POLICY_DOC_SOURCES, POLICY_DOC_TYPES } from '../constants/policy-doc';
import { LoggerSingleton, Category } from '../logger/LoggerSingleton';
import { UICategory, UILoggerSingleton } from '../logger/UILoggerSingleton';

export enum LiteTouchDecision {
  OFFER = 'Offer',
  DECLINE = 'Decline',
  NEEDS_LABS = 'NeedsLabs',
}

@supertypeClass({ toClient: true, toServer: true })
export class Policy extends Everything implements GQLPolicy {
  wasCanceledDeclinedRejected() {
    const workflowState = this.workflowState || this.uwWorkflowState;
    return !!workflowState && /Canceled|Rejected|Declined/.test(workflowState);
  }

  wasCanceledDeclined() {
    const workflowState = this.workflowState || this.uwWorkflowState;
    return !!workflowState && /Canceled|Declined/.test(workflowState);
  }

  wasCanceled() {
    const workflowState = this.workflowState || this.uwWorkflowState;
    return !!workflowState && /Canceled/.test(workflowState);
  }

  wasTerminated() {
    const workflowState = this.workflowState;
    return !!workflowState && /Terminated/.test(workflowState);
  }

  /**
   * Check whether a policy is cancelled already or a request for cancellation has been issued
   */
  toBeCanceledDeclinedRejected() {
    return (
      this.wasCanceledDeclinedRejected() ||
      (this.futureWorkflowState && /Canceled|Rejected|Declined/.test(this.futureWorkflowState))
    );
  }

  get isPrimary() {
    return this.type === 'primary';
  }

  get isSecondary() {
    return this.type === 'secondary';
  }

  get isSecondaryWithPendingAmendment() {
    return this.typeWithPendingAmendment() === 'secondary';
  }

  get isPrimaryWithPendingAmendment() {
    return this.typeWithPendingAmendment() === 'primary';
  }

  @property({ isLocal: true, persist: false })
  reasonForInsuranceTrigger;

  @property({ isLocal: true, persist: false })
  premiumSourceTrigger;

  @property({ isLocal: true, persist: false })
  collaterallyAssignedTrigger;

  @property({ isLocal: true, persist: false })
  economicIncentiveTrigger;

  @property({ isLocal: true, persist: false })
  beneficialInterestTrigger;

  static STATUS = {
    ABANDONED: 'Abandoned',
    AGENCY_DECLINED_E_SUBMIT: 'Agency Declined to e-Submit',
    E_SUBMITTED: 'Application e-Submitted',
    AWAITING_APPROVAL: 'Awaiting Agency Approval',
    AWAITING_AGENT_SIGNATURE: 'Awaiting Agent e-Signature',
    AWAITING_CONSUMER_SIGNATURE: 'Awaiting Consumer Signature',
    CONSUMER_DECLINED: 'Consumer Declined to e-Sign',
    LINK_EXPIRED: 'e-Signature Link Expired',
    E_SUBMITTED_WET: 'e-Submitted with Wet Signatures',
    E_SUBMITTED_ACK: 'e-Submitted - Confirm. Sent',
    PRINT_TO_SIGN: 'Print for Wet Signatures',
    STARTED: 'Started',
    SYNC_TO_SUBMIT: 'Sync to e-Submit',
    PENDING_APPROVAL: 'Pending Principal Agent Approval',
    DECLINED: 'Declined by Principal Agent',
  };

  static UW_TYPES = {
    AU: { code: 'Accelerated', desc: 'Accelerated' },
    Algo: { code: 'Algorithmic', desc: 'Algorithmic' },
    Manual: { code: 'Manual', desc: 'Manual' },
  };

  // this is a helper variable instead of a launch darkly config because there
  // is no connecton from policy back to the controller, or the FeatureFlags
  // object, where the launch darkly flags live
  @property({ toServer: false, persist: false })
  isJointOwnerEnabledFlag = true;

  @property({
    logChanges: true,
    sensitiveData: true,
    toServer: false,
    comment: 'Policy number, this is the id that customer can see and use to retrieve policy information',
  })
  policyNumber: string;

  @property({
    getType: () => Application,
    comment: 'ref to Application table, which holds application number and policies under that application',
  })
  application: Application;

  @property()
  type: string;

  /*
   * convertedTo and convertedFrom are properties that are used when a Term policy is converted to WL.
   * convertedFrom will be set on the converted WL policy. It will be the policy number of the original
   * Term policy. For this policy, convertedTo will be null.
   * convertedTo will be set on the original Term policy. It will be the policy number of the newly created
   * WL policy. For this policy, convertedFrom will be null.
   */
  @property({
    comment: 'Policy number of the Term policy that is converted to WL policy',
  })
  convertedFrom: string;

  @property({
    comment: 'Policy number of the WL policy that has been converted from a Term policy',
  })
  convertedTo: string;

  @property({ comment: 'Carrier of applied policy. Domain of values {HAVE: MassMutual, CAS: MassMutual}' })
  carrier: string;

  @property()
  hasSeenCongrats: boolean;

  @property({ values: LiteTouchDecision })
  liteTouchDecision: string;

  @property()
  isLiteTouch: boolean;

  @property({
    comment:
      'Dedicated for CAS policies, this is the date for agent generate the link and send to customer to complete the policy application',
  })
  linkSentDate: Date;

  @property({
    comment: 'Date when the link for completing policy was opened by customer, for CAS policies only',
  })
  linkOpenedDate: Date;

  @property({
    comment: 'product code, such as CASTerm',
  })
  product: string = 'CASTerm';

  @property({
    comment: 'type of product, such as Term or Whole',
  })
  productType: string = 'Term';

  @property()
  submittedLanguage: string = 'en';

  @property({
    comment: 'Message being sent to customer along with the link from agent to complete policy application, CAS only',
  })
  personalizedMessage;

  @property({
    logChanges: true,
    values: ['Issued', 'Active', 'LapsePending', 'NotTaken', 'Canceled', 'Rescinded'],
    comment: 'Temporary Life Insurance Certificate Status',
  })
  tLICStatus: string;

  @property({
    logChanges: true,
    values: ['Issued', 'Active', 'LapsePending', 'Lapsed', 'NotTaken', 'Canceled', 'FreeLooked', 'Rescinded'],
  })
  status: string;

  @property({
    logChanges: true,
    comment: 'Date time when application was submitted',
  })
  submittedAt: Date;

  @property({
    comment: "IP address of customer's device when application was submitted",
  })
  submittedFromIP: string;

  @property({
    comment: 'Within this Date, policy can be reinstated',
  })
  canBeReinstatedUntil: Date;

  @property()
  issuedDate: Date;

  @property({
    comment: 'Date time of sending Issue Policy request to Policy Admin ',
  })
  issuePolicyRequestDate: Date;

  @property()
  willLapseOn: Date;

  @property({
    comment: "Date time when policy's freelook period ends which started from issue date",
  })
  freeLookEndsOn: Date;

  @property()
  paramedRequiredBy: Date;

  @property({
    comment: 'Date time that customer scheduled for para med ',
  })
  paramedScheduledDate: Date;

  @property({
    comment: 'Date user scheduled paramed through online lab scheduler',
  })
  onlineParamedScheduledDate: Date;

  @property({
    comment: 'Date user did the scheduling of paramed through online lab scheduler',
  })
  onlineParamedSchedulingDate: Date;

  @property({
    isLocal: true,
    comment: 'key for scheduling exam appointments with exam one, non-persistable',
  })
  scheduleNowAttendeeCode: string;

  @property()
  offerRequiredBy: Date;

  @property()
  offeredDate: Date;

  @property({
    comment: 'For accelerated underwritten policies, the date when the policy contract was signed by customer',
  })
  aUEffectiveDate: Date;

  @property({
    comment: 'Date time that a tlic policy take effect',
  })
  tLICEffectiveDate: Date;

  @property({
    comment: 'Date time that a policy take effect',
  })
  effectiveDate: Date;

  @property({
    comment:
      'Date time that customer has to accept offer by, offer includes algo underwritting policy, TLIC and mannual underwriting policy',
  })
  finalDecisionRequiredBy: Date;

  @property({
    comment: 'For RARE policy, it has beneficiary who is minor',
  })
  isMinor: boolean; // new in RARE

  @property({
    comment: 'Free look period duration',
  })
  freeLookPeriodDays: number = 10;

  @property({
    comment: 'Boolean, indicate whether we should generate policy packet as physical mail',
  })
  mustMailPaperPacket: boolean;

  @property()
  isSaveAge: boolean;

  @property()
  resendAdditionalPacket: boolean;

  @property({ isLocal: true })
  private _reasonforInsuranceIsBusiness: boolean;

  @property({
    toServer: false,
    comment: 'internal flag, indicate whether customer can make change after submitting policy',
  })
  allowChangesPostSubmission: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'reason for insurance is investment or business',
  })
  directReasonInsuranceIsBusiness: boolean;

  @property({ isLocal: true })
  get reasonforInsuranceIsBusiness(): boolean {
    if (this.reasonForInsurance === 'B' || this.reasonForInsurance === 'IB') {
      return true;
    } else if (this.reasonForInsurance === null) {
      return null;
    } else if (this.reasonForInsurance === undefined) {
      return undefined;
    } else {
      return false;
    }
  }

  set reasonforInsuranceIsBusiness(value: boolean) {
    if (value) {
      if (!(this.reasonForInsurance === 'B' || this.reasonForInsurance === 'IB')) {
        this.reasonForInsurance = 'IB';
      }
    } else if (value === null) {
      this.reasonForInsurance = null;
    } else if (value === undefined) {
      this.reasonForInsurance = undefined;
    } else if (this.reasonForInsurance === 'B' || this.reasonForInsurance === 'IB') {
      this.reasonForInsurance = 'U';
    }
  }

  @property()
  notOnMilitaryAndForCollateral: boolean;

  @property({
    comment: 'Reason for unemployed coverage',
  })
  reasonForUnemployedCoverage: string;

  @property({
    values: ['true', 'false'],
    descriptions: {
      true: 'Yes',
      false: 'No',
    },
    comment: 'Boolean indicate whether insured, owner and payer are the same person, could be true, false and null',
  })
  insuredAndOwnerAndPayer: boolean;

  @property({
    toServer: false,
    comment: 'UI flag for whether showing policy in account center',
  })
  showInAccountCenter: boolean = true;

  @property({ isLocal: true })
  isValidating: boolean;

  @property({
    type: Array,
    getType: () => String,
    comment:
      'Array of reasons that policy application was rejected, the reasons are codes stored in RejecteReasons.ts file',
  })
  rejectedReasons: Array<string> = [];

  @property({ isLocal: true })
  _selfInsured: string;

  @property({
    isLocal: true,
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Determines if the insured is self or someone else, not a persisted property',
  })
  set selfInsured(value) {
    if (!value) {
      return;
    } else if (value === 'true') {
      this.insuredType = 'you';
    } else {
      this.insuredType = null;
    }
    this._selfInsured = value;
  }

  get selfInsured(): string {
    if (!this.insuredType) {
      return this._selfInsured;
    }
    return this.insuredType === 'you' ? 'true' : 'false';
  }

  @property({
    rule: 'required',
    values: ['you', 'spouse', 'partnerfiance'],
    descriptions: function () {
      const desc = { you: 'Yes', spouse: 'No, my spouse', partnerfiance: 'No, my partner or fiance' };
      return desc;
    },
    comment: 'Insured Type indicated if the owner or spouse or someone else is being insured',
  })
  insuredType: string;

  @property({ rule: 'required', values: ['FP', 'O'], descriptions: { FP: 'Yes', O: "No, it's for other purposes" } })
  reasonForInsuranceShort: string;

  @property({
    rule: 'required',
    values: ['FI', 'P', 'R', 'E', 'I', 'EP', 'B', 'O', 'N', 'U'],
    descriptions: {
      FI: 'Family Income',
      P: 'Personal',
      R: 'Retirement',
      E: 'Education',
      I: 'Investment',
      EP: 'Estate Planning',
      B: 'Business',
      O: 'Other',
      N: 'None',
      IB: 'Investment or Business',
      U: 'Unknown',
    },
    comment:
      "Detailed Reason for purchasing Insurance. If reasonForInsuranceShort = 'O', then this " +
      'field contains code for additional detail. Domain of values: { "FI": "Family Income",  "P": "Personal", ' +
      ' "R": "Retirement",  "E": "Education",  "I": "Investment", "EP": "Estate Planning",  "B": "Business",  ' +
      '"O": "Other", "N": "None", "IB": "Investment or Business", "U": "Unknown"}',
  })
  reasonForInsurance: string;

  @property({ type: Array, getType: () => Beneficiary })
  primaryBeneficiary: Array<Beneficiary> = [];

  @property({ type: Array, getType: () => ESignDocMetadata })
  eSignDocMetadata: Array<ESignDocMetadata> = [];

  getESignAppPacketMetadata(): ESignDocMetadataType {
    const appPacket = this.getApplicationPacketName();
    const appPacketMetadata = this.eSignDocMetadata.filter(
      (docMetadata: ESignDocMetadata) => docMetadata.docType === appPacket
    );
    return appPacketMetadata && appPacketMetadata[0] && appPacketMetadata[0].getMetadata();
  }

  //used for Inhouse - ESign( Signataur ) to add detail for eSign Certificate.
  addDocMetadata(docType: DocTypes): ESignDocMetadata {
    //add new doc Metadata for appPacket
    const eSignDocMetadata = this.eSignDocMetadata;
    const newDocMetadata = ESignDocMetadata.new(docType, this);
    eSignDocMetadata.push(newDocMetadata);
    this.eSignDocMetadata = eSignDocMetadata;
    return newDocMetadata;
  }

  /**
   * HL-15833: Updated values and descriptions for this prop are maintained in App Question Metadata
   */
  @property({
    rule: 'required',
    values: function () {
      const vals = ['9', '13', '1', '2', '6'];

      if (this.isOwnerSameAsInsured()) {
        vals.push('7');
      }

      return vals;
    },
    descriptions: function () {
      let spouse = 'spouse';

      if (this.insuredType === 'partnerfiance') {
        spouse = 'partner or fiance';
      }

      let insert = '';
      let insertLC = '';

      if (this.isOwnerSameAsInsured()) {
        if (this.insuredType === 'you') {
          spouse = 'Your spouse or partner/fiance';
          insert = 'Your';
          insertLC = 'your';
        } else {
          spouse = this.insured.person.firstName + "'s " + spouse;
          insert = insertLC = this.insured.person.firstName + "'s";
        }
      } else {
        insert = insertLC = this.insured.person.firstName + "'s";
        spouse = this.owner.getFullNameOfOwner();
      }

      const ret = {};

      ret['9'] = spouse === ' ' ? 'Spouse or partner/fiance' : spouse;
      ret['13'] = insert + ' children';
      ret['1'] = 'Other named individuals';
      ret['2'] = insert + ' estate';
      ret['6'] = 'A trust under ' + insertLC + ' will';
      ret['7'] = 'Other trust';

      return ret;
    },
    comment:
      'Beneficiary type code. Domain of Values: 1 - "Other named individuals", ' +
      '2 - "Your estate", 6 - "A trust under your will", 7 - "Other trust",' +
      '9 - "Spouse/Partner/Fiance", 13 - "Your children" ',
  })
  beneficiaryType: string = '9';

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'Boolean, if true, that beneficiary can pass their share to their heir if they passed away before insured.Per Policy',
  })
  beneficiaryPerStirpes: boolean;

  @property({
    rule: ['required'],
    values: ['15', '12'],
    descriptions: { '12': 'Only children from a specific marriage', '15': 'All lawful children' },
    comment:
      'If beneficiaryType = "13 - Your Children" Additional codes for which children. ' +
      'Domain of values - {"12": "Only children from a specific marriage", "15": "All lawful children"}',
  })
  beneficiaryChildrenType: string;

  @property({
    rule: ['required'],
    comment: 'If beneficiaryChildrenType = "12 - Only children from a specific marriage". User input of last name',
  })
  beneficiaryOtherParentName: string;

  @property({
    rule: ['required'],
    values: ['false', 'true'],
    descriptions: { false: 'No', true: 'Yes' },
    comment: 'This is set to true if user wants to evenly split between all children selected',
  })
  beneficiaryChildrenEvenSplit: boolean;

  @property({
    rule: ['required'],
    values: ['false', 'true'],
    descriptions: { false: 'No', true: 'Yes' },
    comment: 'This is set to true if policy has a contingent Beneficiary',
  })
  hasContingentBeneficiary: boolean;

  @property({
    rule: 'required',
    values: function () {
      let ret = this.__values__('beneficiaryType');
      ret = _.without(ret, '2');
      ret = _.without(ret, '6');
      if (!this.isOwnerSameAsInsured()) {
        ret = _.without(ret, '7');
      }
      if (this.beneficiaryType === '9') {
        ret = _.without(ret, '9');
      }
      return ret;
    },
    descriptions: function () {
      return this.__descriptions__('beneficiaryType');
    },
  })
  contingentBeneficiaryType: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'True if user elects to have the amount the contingent beneficiary would have received be divided ' +
      'among their descendants if they die before you',
  })
  contingentBeneficiaryPerStirpes: boolean;

  @property({
    rule: ['required'],
    values: ['15', '12'],
    descriptions: { '12': 'Only children from a specific marriage', '15': 'All lawful children' },
    comment:
      'If contingentBeneficiaryType = "13 - Your Children", this gives additional codes as to which children.' +
      'Domain of Values - {"12": "Only children from a specific marriage", "15": "All lawful children"}',
  })
  contingentBeneficiaryChildrenType: string;

  @property({
    rule: ['required'],
    comment: 'If contingentBeneficiaryChildrenType = "12 - Only children from a specific marriage" User input of name',
  })
  contingentBeneficiaryOtherParentName: string;

  @property({
    rule: ['required'],
    values: ['false', 'true'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'This is set as true if user wants to evenly split between all children selected',
  })
  contingentBeneficiaryChildrenEvenSplit: boolean;

  @property({ type: Array, getType: () => Beneficiary })
  contingentBeneficiary: Array<Beneficiary> = [];

  @property({
    rule: ['required'],
    comment: 'Relationship to the insured',
  })
  contingentOwnerType: string;

  @property({
    comment: 'Contingent Owner Person',
  })
  contingentOwnerPerson: Person;

  @property({
    getType: () => PolicyAgent,
    comment: 'point to policyAgent table, which contains the agent information, such as license, commission ratio',
  })
  primaryPolicyAgent: PolicyAgent;

  @property({
    getType: () => Party,
    comment: "ref to party table which contains secondary adressee's information",
  })
  secondaryAddressee: Party;

  get secondaryAddresseePerson(): Person | null {
    return this.secondaryAddressee && this.secondaryAddressee instanceof Person ? this.secondaryAddressee : null;
  }

  set secondaryAddresseePerson(person: Person) {
    this.secondaryAddressee = person;
  }

  @property({ type: Array, getType: () => PolicyAgent })
  additionalPolicyAgents: Array<PolicyAgent> = [];

  @property({
    rule: ['required'],
    values: ['false', 'true'],
    descriptions: { false: 'No', true: 'Yes' },
    comment:
      'when flag is true, If any beneficiaries are still minors when they receive the insurance proceeds, someone should be appointed to manage it for them',
  })
  needCustodian: boolean;

  @property({ rule: 'required', comment: 'Last name of the custodian' })
  custodianLastName: string;

  @property({ rule: 'required', comment: 'First name of the custodian' })
  custodianFirstName: string;

  @property({ rule: 'required', values: Assumptions.stateValues, comment: 'Residency State of the Custodian' })
  custodianState: string;

  @property()
  ownerClassification: string;

  @property({
    isLocal: true,
  })
  set hasGirRider(value: string) {
    if (value === 'true') {
      this.addRider(RiderType.GIR);
    } else {
      this.removeRider(RiderType.GIR);
    }
  }

  get hasGirRider(): string {
    if (this.getRider(RiderType.GIR)) {
      return 'true';
    } else {
      return 'false';
    }
  }

  @property({
    isLocal: true,
  })
  set hasGirRiderBool(value: boolean) {
    this.hasGirRider = value ? 'true' : 'false';
  }

  get hasGirRiderBool(): boolean {
    return this.hasGirRider === 'true' ? true : false;
  }

  @property({
    isLocal: true,
    rule: ['currencyRounded'],
  })
  set girRiderAmount(value: number) {
    const girRider: PolicyRider = this.getRider(RiderType.GIR);
    if (girRider) {
      // the amount passed in here has already been validated by UI validation rules.
      girRider.amount = value;
    }
  }

  get girRiderAmount(): number {
    const girRider: PolicyRider = this.getRider(RiderType.GIR);
    return girRider ? girRider.amount : null;
  }

  @property({
    isLocal: true,
  })
  set hasRtrRiderBool(value: boolean) {
    this.hasRtrRider = value ? 'true' : 'false';
  }

  get hasRtrRiderBool(): boolean {
    return this.hasRtrRider === 'true' ? true : false;
  }

  @property({
    isLocal: true,
  })
  set hasRtrRider(value: string) {
    if (value === 'true') {
      this.addRider(RiderType.RTR);
    } else {
      this.removeRider(RiderType.RTR);
    }
  }

  get hasRtrRider(): string {
    if (this.getRider(RiderType.RTR)) {
      return 'true';
    } else {
      return 'false';
    }
  }

  @property({
    isLocal: true,
    rule: ['currencyRounded'],
  })
  set rtrRiderAmount(value: number) {
    const rtrRider: PolicyRider = this.getRider(RiderType.RTR);
    if (rtrRider) {
      // the amount passed in here has already been validated by UI validation rules.
      rtrRider.amount = value;
    }
  }

  get rtrRiderAmount(): number {
    const rtrRider: PolicyRider = this.getRider(RiderType.RTR);
    return rtrRider ? rtrRider.amount : null;
  }

  @property({
    isLocal: true,
  })
  get girRider(): PolicyRider {
    return (this.riders || []).find((rider) => {
      return rider.type === RiderType.GIR;
    });
  }

  set girRider(rider: PolicyRider) {
    if (rider) {
      this.riders.push(rider);
    } else {
      this.removeRider(RiderType.GIR);
    }
  }

  @property({
    isLocal: true,
  })
  get rtrRider(): PolicyRider {
    return (this.riders || []).find((rider) => {
      return rider.type === RiderType.RTR;
    });
  }

  set rtrRider(rider: PolicyRider) {
    if (rider) {
      this.riders.push(rider);
    } else {
      this.removeRider(RiderType.RTR);
    }
  }

  getRider(type: RiderType): PolicyRider {
    if (this.riders) {
      return this.riders.find((rider) => {
        return rider.type === type;
      });
    }
  }

  addRider(type: RiderType): PolicyRider {
    let rider = this.getRider(type);
    if (!rider) {
      rider = PolicyRider.new(type, this);
      this.riders.push(rider);
    }
    return rider;
  }

  removeRider(type: RiderType): boolean {
    for (let idx = 0; idx < this.riders.length; ++idx) {
      const rider = this.riders[idx];
      if (rider.type === type) {
        rider.remove();
        this.riders.splice(idx, 1);
        return true;
      }
    }
    return false;
  }

  getRtrFlatExtras(): Array<FlatExtra> {
    if (this.hasRtrRider === 'true') {
      return this.rtrRider.flatExtras;
    }

    return [];
  }

  /** Overrider in CoverpathBasePolicy */
  isJointOwnership(): boolean {
    return false;
  }

  isSelfParticipatingOwnership() {
    return this.ownerClassification === 'you' || this.ownerClassification === 'joint';
  }

  @property({ isLocal: true })
  _selfOwner: string;

  @property({
    isLocal: true,
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Determines if the owner is self or someone else, not a persisted property',
  })
  set selfOwner(value) {
    if (!!value === false) {
      return;
    } else if (value === 'you') {
      this.ownerType = 'you';
    } else {
      this.ownerType = null;
    }
    this._selfOwner = value;

    this.onSelfOwnerChange();
  }

  get selfOwner(): string {
    // workaround for hardcoding this value for LifeBridge
    if (this.isLifeBridge()) {
      this.ownerType = 'trust';
      return 'trust';
    }

    if (!!this.ownerClassification === false || this.isSelfParticipatingOwnership()) {
      return 'true';
    }

    switch (this.ownerClassification) {
      case 'you':
        return 'true';
      case 'trust':
        return 'trust';
      default:
        return 'false';
    }
  }

  onSelfOwnerChange() {
    if (this.selfOwner === 'true') {
      if (!this.owner.email) {
        this.owner.email = this.customer.email;
      }
      this.owner.emailTrigger();
    }
    this.setOwnerObject();
  }

  private setOwnerObject() {
    const origOwnerState = this.ownerPersonResidentialAddress ? this.ownerPersonResidentialAddress.state : undefined;
    if (this.selfOwner === 'trust') {
      if (this.ownerEntity === null) {
        this.ownerType = 'trust';
        this.trustOrOtherEntity = Entity.new(this.customer, false, this);
        this.trustOrOtherEntity.type = null;
        this.owner = this.trustOrOtherEntity;
      }
    } else if (this.ownerPerson === null) {
      this.owner = Person.new(this.customer);
      this.ownerPersonResidentialAddress = Address.new(this.customer);
      this.ownerPersonPhone = Phone.new(this.customer);
      this.trustOrOtherEntity = null;
    }

    // If the owner's state is undefined, set it
    if (this.ownerPersonResidentialAddress && !this.ownerPersonResidentialAddress.state) {
      this.ownerPersonResidentialAddress.state = origOwnerState ? origOwnerState : this.insured.address.state;
    }
  }

  @property({
    rule: 'required',
    values: function () {
      return this.ownerTypeValues();
    },
    descriptions: function () {
      const desc = {
        you: { text: 'Yes', width: '2' },
        spouse: { text: 'No, my spouse', width: '4' },
        partnerfiance: { text: 'No, my partner or fiance', width: '6' },
      };
      return desc;
    },
    comment: 'Owner type of policy',
  })
  ownerTypeSelection: string;

  @property({
    rule: 'required',
    values: function () {
      return this.ownerTypeValues();
    },
    descriptions: function () {
      const desc = {
        you: { text: 'Yes', width: '2' },
        spouse: { text: 'No, my spouse', width: '4' },
        partnerfiance: { text: 'No, my partner or fiance', width: '6' },
      };
      return desc;
    },
    comment: 'Owner type of policy',
  })
  ownerType: string;

  @property({
    rule: ['required'],
    values: ['insured', 'ownerEstate'],
    descriptions: function () {
      return {
        insured: this.getInsuredPronoun().replace(/you/i, 'You'),
        ownerEstate: (this.getOwnerPossessivePronoun() + ' Estate').replace(/you/i, 'You'),
      };
    },
    comment:
      'Selection of who ownership will be transferred to in case of death of owner. Only when owner is not insured',
  })
  ownerDelegate: string;

  ownerDelegateTrigger(): void {
    if (this.ownerDelegate === 'contingent') {
      this.contingentOwnerPerson = Person.new(this.customer);
      this.contingentOwnerPerson.residentialAddress = Address.new(this.customer);
      this.contingentOwnerPerson.phone = Phone.new(this.customer);
    } else {
      this.contingentOwnerPerson = undefined;
    }
  }

  @property({ rule: ['required'] })
  ownerRelationship: string;

  @property()
  isJointOwnerSomeoneElse: string;

  @property({ getType: () => Person })
  otherOwner: Person;

  @property({ values: { false: 'No', true: 'Yes' } })
  declarationIncentive: boolean;

  @property({ values: { false: 'No', true: 'Yes' } })
  declarationSellTransfer: boolean;

  @property({ values: { false: 'No', true: 'Yes' } })
  declarationOwnedCaptive: boolean;

  @property()
  declarationPaidCaptive: boolean;

  @property({ isLocal: true })
  declarationPaidCaptiveTrustValues = { false: 'No', true: 'Yes' };

  @property({
    values: ['insured', 'owner'],
    descriptions: function () {
      return {
        insured: this.insured.person.getFullName(),
        owner: this.owner.getFullNameOfOwner(),
      };
    },
    comment: 'Person who pays for the premium, such as owner or insured',
  })
  premiumPayer: string = 'owner';

  @property({
    getType: () => Party,
    comment: 'ref to Party table, which includes payer party information',
  })
  payer: Party;

  @property({
    comment: 'designates whether the Owner is also the Payer',
  })
  isOwnerAlsoPayer: string;

  showPayerEntityOrPerson(): boolean {
    return this.showPayerPerson() || this.showPayerEntity();
  }

  showPayerPerson(): boolean {
    return this.premiumPayer === PayerTypeOptions.anotherPerson && this.isOwnerAlsoPayer === 'false';
  }

  showPayerEntity(): boolean {
    return this.premiumPayer === PayerTypeOptions.entity && this.isOwnerAlsoPayer === 'false';
  }

  @property({
    rule: 'required',
    values: ['false', 'true'],
    descriptions: { false: 'No (I am using my income, savings or gifts)', true: 'Yes (I am using other sources)' },
    comment:
      "Boolean, 'true' means customer will be using money to pay for this policy that is not coming from either income, savings/investments, or gifts/inheritances ",
  })
  premiumSource: boolean;

  @property({ rule: 'required' })
  premiumSourceDetails: string;

  @property({
    getType: () => PremiumLoan,
    rule: 'required',
    comment: 'The details of the premium loan if there is one',
  })
  premiumLoan: PremiumLoan;

  @property({
    values: {
      income: 'Income for dependents',
      estatetax: 'Pay estate taxes',
      mortgage: 'Pay off mortgage',
      other: 'Other',
    },
  })
  personalNeeds: string = 'income';

  @property()
  personalNeedsOther: string;

  @property({
    values: {
      employee: 'Key employee',
      stock: 'stock redemption',
      cross: 'Cross purchase',
      other: 'Other',
    },
  })
  businessNeeds: string = 'employee';

  @property()
  businessNeedsOther: string;

  @property({ values: { ach: 'Direct debit', cc: 'Credit card', check: 'Check' }, comment: 'Mode of premium payments' })
  paymentMode: string = 'ach';

  @property({ values: { ach: 'Direct debit', cc: 'Credit card' }, comment: 'Payment mode for the first payment' })
  firstPaymentMode: string = 'ach';

  @property({ rule: ['required', 'text'], logChanges: true, comment: "Payer's bank name for ach" })
  paymentBankName: string;

  @property({
    rule: ['required', 'numericstring'],
    length: 9,
    validate: 'isMinLength(9);isMaxLength(9)',
    logChanges: true,
    comment: "Payer's ABA.",
  })
  paymentABA: string;

  @property({
    rule: ['required', 'numericstring'],
    validate: 'isMaxLength(17)',
    logChanges: true,
    sensitiveData: true,
    comment: "Payer's account number",
  })
  paymentAccount: string;

  @property({
    rule: ['numericstring'],
    validate: 'isMaxLength(22)',
    logChanges: true,
    sensitiveData: true,
    comment: "Payer's CreditCard Profile ID",
  })
  paymentCCProfileId: string;

  @property({
    rule: ['text'],
    validate: 'isMaxLength(2)',
    logChanges: true,
    sensitiveData: true,
    comment: "Payer's CreditCard Type",
  })
  paymentCCCardType: string;

  @property({
    rule: ['required'],
    values: function () {
      return ['2', '1'];
    },
    descriptions: {
      '1': 'Savings',
      '2': 'Checking',
      '3': 'Personal Checking',
      '4': 'Personal Savings',
      '5': 'Business Checking',
      '6': 'Business Savings',
    },
    comment: "payment account type, values='1:Checking,2:Saving'",
  })
  paymentAccountType: string;

  @property({
    values: { individual: 'Individual', joint: 'Joint', entity: 'Entity' },
    comment: "payment account owner's type",
  })
  paymentAccountOwnerType: string = 'individual';

  @property()
  paymentAccountOwner: string;

  @property({
    values: {
      monthly: 'Monthly',
      annually: 'Annually',
      semiAnnual: 'Twice yearly',
      quarterly: 'Quarterly',
    },
    comment: 'Frequency of Payments',
  })
  paymentFrequency: string = 'monthly';

  @property({
    rule: ['required'],
    comment: 'The date of a month that you make payment',
  })
  paymentDay: number;

  get paymentInformationGathered() {
    return !!this.paymentBankName && !!this.paymentAccount && !!this.paymentABA && !!this.paymentAccountType;
  }

  @property()
  hipaaAuthorizationViewed: boolean;

  @property({ values: { false: 'No', true: 'Yes' } })
  conditionalCoverage: boolean;

  @property({
    rule: ['required'],
    values: ['false', 'true'],
    descriptions: { false: 'No', true: 'Yes' },
    comment: 'This is set to . true if waiver rider was opted for',
  })
  waiver: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      "Boolean, 'true' means that the applicant is setting up a conditional assignment, appointing a lender as the primary beneficiary of a death benefit to use as collateral for a loan.",
  })
  collaterallyAssigned: boolean = false;

  @property({
    comment: 'Collateral Assignment related data',
  })
  collateralAssignment: CollateralAssignment;

  @property({
    comment: 'Business Purpose data',
  })
  policyBusinessPurpose: PolicyBusinessPurpose;

  @property({
    rule: ['required'],
    values: ['false', 'true'],
    descriptions: { false: 'No', true: 'Yes' },
    comment:
      "Boolean, 'true' means customer's spouse and/or customer has been offered any economic incentive such as free life insurance or money to purchase this policy or entered into any arrangement that entitles a lender or investor to any portion of the death benefit beyond a loan repayment.",
  })
  economicIncentive: boolean;

  @property({
    rule: ['required'],
    values: ['false', 'true'],
    descriptions: { false: 'No', true: 'Yes' },
    comment:
      "Boolean, 'true' means customer's spouse and/or customer has a current agreement or commitment to sell, transfer, assign or release this policy or any beneficial interest of this policy or its ownership structure",
  })
  beneficialInterest: boolean;

  @property({ values: { false: 'No', true: 'Yes' } })
  turnedDown: boolean;

  @property()
  premiumAmount: number;

  @property()
  flatExtras: number;

  @property({
    comment:
      'When this flag is true, after policy issued, we will request an Attending Physicians Statement from health care providers',
  })
  postIssueApsFlag: boolean;

  @property({
    comment: 'the maximum face amount for TLIC policy',
  })
  tLICFaceAmountCap: number = Product.productSpecs.CASTerm.maxFaceForTLIC;

  @property({
    comment: 'Date time when application was signed by customer',
  })
  applicationSignedDate: Date;

  @property({
    comment: 'State where application was signed by customer',
  })
  applicationSignedState: string;

  @property()
  initialAcceleratedRateClass: string;

  @property({
    comment: 'underwriting class for accelerated offer',
  })
  acceleratedRateClass: string;

  @property({
    comment: 'underwriting rate class for tlic policy',
  })
  tLICRateClass: string;

  /* If the TLIC rate class we give differs from the output*/
  @property()
  tLICAlgoRateClass: string;

  @property({
    comment: 'underwriting rate class for algo policy',
  })
  finalAlgoRateClass: string;

  @property({
    comment: 'Final underwriting class for manual underwritten policy',
  })
  finalManualRateClass: string;

  @property({
    comment: 'underwriting rate class for policy',
  })
  finalRateClass: string;

  @property({
    getType: () => TLICPremiumBreakdown,
    comment:
      'Foreign Key to join to premium_breakdown in order to obtain premium information related to Temporary Life Insurance Coverage Offer/Policy',
  })
  tLICPremiumBreakdown: TLICPremiumBreakdown;

  @property({
    getType: () => PremiumBreakdown,
    comment:
      'Foreign Key to join to premium_breakdown in order to obtain premium information related to Algorithmic Offer/Policy',
  })
  finalAlgoPremiumBreakdown: PremiumBreakdown;

  @property({
    getType: () => PremiumBreakdown,
    comment: 'A premium breakdown that represents the values of the policy at submission time',
  })
  appliedForPremiumBreakdown: PremiumBreakdown;

  @property({
    getType: () => PremiumBreakdown,
    comment:
      'Foreign Key to join to premium_breakdown in order to obtain premium information related to Final Offer/Policy',
  })
  premiumBreakdown: PremiumBreakdown;

  @property({
    comment:
      'Premium for waiver rider. Use the waiver amount present in ' +
      'premium_breakdown.waiver/PremiumBreakdown.waiver rather than using this',
  })
  waiverPremium: number;

  @property({
    comment: 'For Haven, this is the quote provided to customer.',
  })
  selectedQuote;

  @property({
    rule: ['currency'],
    values: [
      '$100,000',
      '$200,000',
      '$300,000',
      '$400,000',
      '$500,000',
      '$600,000',
      '$700,000',
      '$800,000',
      '$900,000',
      '$1,000,000',
      '$1,200,000',
      '$1,400,000',
      '$1,500,000',
      '$1,700,000',
      '$1,900,000',
      '$2,000,000',
      '$2,500,000',
      '$3,000,000',
    ],
  })
  overrideAmount: number;

  @property({
    rule: ['numeric'],
    values: [5, 10, 15, 20, 25, 30],
    descriptions: {
      '5': '5 years',
      '10': '10 years',
      '15': '15 years',
      '20': '20 years',
      '25': '25 years',
      '30': '30 years',
    },
  })
  overrideTerm: number = 20;

  @property()
  policyHidden: boolean;

  @property()
  policyAppliedFor: boolean;

  @property()
  idCheckAttempts: number = 0;

  @property({
    comment: 'amount of attempts that customer did for checking driving record information via webservice',
  })
  drivingRecordAttempts: number;

  @property({ getType: () => PolicyDoc })
  policyDocs: Array<PolicyDoc> = [];

  @property({
    getType: () => IllustrationRequestProperty,
    toClient: false,
    toServer: false,
  })
  illustrationRequestProperties: Array<IllustrationRequestProperty> = [];

  @property({ logChanges: true })
  workflowState: string;

  @property()
  futureWorkflowState: string;

  @property({ logChanges: true })
  workflowSubState: string;

  @property()
  futureWorkflowSubState: string;

  @property({ logChanges: true })
  uwWorkflowState: string;

  @property({ logChanges: true })
  uwWorkflowSubState: string;

  @property()
  workflowStateUpdate: Date;

  @property({ type: Array, getType: () => String })
  currentStageRequirements: Array<string> = [];

  @property({ type: Array, getType: () => WorkflowStateHistory })
  workflowStateHistory: Array<WorkflowStateHistory> = [];

  @property({ type: Array, getType: () => FollowUp })
  followUps: Array<FollowUp> = [];

  @property({
    toServer: false,
    comment: 'for cas, ID of invitation for customer to complete policy application',
  })
  invite: string;

  @property({ isLocal: true })
  ownerPackage: PackageQuery;

  @property({
    getType: () => Customer,
    comment: "ref to customer table, account owner's information, might not be policy owner or insured",
  })
  customer: Customer;

  @property({ type: Array, getType: () => PolicyChangeHistory })
  policyChanges: Array<PolicyChangeHistory> = [];

  @property({
    values: ['Accelerated', 'Algorithmic', 'Manual'],
    comment: 'final underwriting type, values: Accelerated, Algorithmic, Manual',
  })
  finalUWType: string;

  @property({ toClient: false, persist: false, getType: () => UniversalBeneficiary })
  _docusign_getMMBeneficiariesPageVarying_: Array<UniversalBeneficiary>;

  // TODO: Investigate a general way to retrieve lastUpdatedTime on any object instead
  // of adding this property to individual classes
  // https://massmutual.atlassian.net/browse/HL-13216
  @property({ toServer: false })
  lastUpdatedTime: Date;

  @property({
    comment: 'Trust or other Entity',
  })
  trustOrOtherEntity: Entity;

  @property({ getType: () => PolicyRider, comment: 'All the riders attached to this policy' })
  riders: Array<PolicyRider> = [];

  @remote({ on: 'server' })
  async wasEverCancelled(): Promise<boolean> {
    if (!this.customer._id) {
      return false;
    }
    const policies = await Policy.persistorFetchByQuery(
      {
        $and: [
          {
            customer_id: this.customer._id,
          },
          {
            $or: [{ workflowState: 'Application Canceled' }, { workflowState: 'Application Canceled By User' }],
          },
        ],
      },
      {
        session: this.amorphic,
      }
    );

    return Boolean(policies.length);
  }

  @property({ getType: () => UwReasons })
  uwReasons: UwReasons;

  @property({
    values: ['true', 'false'],
    toServer: true,
    comment: "Boolean, 'true' means that the applicant is opting for Autopay (Automatic payment of policy premium).",
  })
  isAutoPay: boolean = true;

  @property({
    toServer: false,
    toClient: false,
    comment: 'Used in Haven Term 2 applications. Necessary to ensure DBs have same columns',
  })
  sectionOfLastAnsweredQuestion: string;

  @property({
    toServer: false,
    comment: 'Dummy Column - Keeps DTC and CP in sync',
  })
  isIndependentAdvisor: boolean;

  @property({
    toServer: false,
    comment: 'Dummy Column - Keeps DTC and CP in sync',
  })
  offerChangedByIndependentAdvisor: boolean;

  constructor() {
    super();
  }

  static new(customer, type?) {
    return new Policy().init(customer, type);
  }

  init(customer, type?) {
    this.uwReasons = UwReasons.new(this);
    const ONE_MEGADOLLAR = 1e6;
    this.customer = customer;
    this.type = type || POLICY_TYPES.PRIMARY;
    this.waiver = false;

    const CollateralAssignmentClass = CollateralAssignment,
      PolicyBusinessPurposeClass = PolicyBusinessPurpose;

    if (customer) {
      this.insured = customer.primaryCustomer;
      this.application = customer.application;
    }

    this.owner = Person.new(this.customer);

    this.premiumPayerPerson = Person.new(this.customer);
    this.premiumPayerAddress = Address.new(this.customer);
    this.premiumPayerPhone = Phone.new(this.customer);
    this.ownerPersonPhone = Phone.new(this.customer);
    this.ownerPersonResidentialAddress = Address.new(this.customer);
    this.ownerPersonMailingAddress = Address.new(this.customer);
    this.ownerPersonVerificationAddress = Address.new(this.customer);
    this.primaryBeneficiaryAdd();
    this.contingentBeneficiaryAdd();
    this.tLICFaceAmountCap = ONE_MEGADOLLAR;
    this.premiumLoan = PremiumLoan.new(this);
    this.collateralAssignment = CollateralAssignmentClass.new();
    this.policyBusinessPurpose = PolicyBusinessPurposeClass.new();

    this.trustOrOtherEntity = null;
    return this;
  }

  clientInit() {
    this.waiverPremium = null;
  }

  get carrierName() {
    return this.product ? Product.products[this.product].channel.carrier.name : '';
  }

  initSecondaryAddresseePerson(customer: Customer) {
    this.secondaryAddresseePerson = Person.new(customer);
    this.secondaryAddresseePerson.residentialAddress = Address.new(customer);
    if (this.insured && this.insured.address && this.getApplicationState() === 'FL') {
      this.secondaryAddresseePerson.phone = Phone.new(customer);
    }
  }

  initSecondaryAddresseePersonPhone(customer: Customer) {
    this.secondaryAddresseePerson.phone = Phone.new(customer);
  }

  getNameOfInsurer() {
    return 'Massachusetts Mutual Life Insurance Company';
  }

  getProductName() {
    return this.term ? this.getProductInfo().name + ' ' + this.term + ' year policy' : this.getProductInfo().name;
  }

  getProductInfo() {
    return Product.products[this.product];
  }

  getProductTypeInfo() {
    return Product.productTypes[this.productType];
  }

  getApplicationState() {
    let state = this.insured.address.state;

    if (this.ownerPersonResidentialAddress.state) {
      state = this.ownerPersonResidentialAddress.state;
    }
    return state;
  }

  static getApplicationState(insuredState, ownerState, insuredType, ownerType) {
    let state = insuredState;
    if ((insuredType !== 'you' || insuredType !== ownerType) && ownerState) {
      state = ownerState;
    }

    return state;
  }

  showBasedOnOccupation() {
    //TODO: Rename function to 'isXXXXX' since return value is boolean (HL-5510)
    const financials = this.insured.financials;
    const allOccupation = Array();

    _.map(financials.__values__('occupation'), function (value: { listValue: string; fieldValue: string }) {
      allOccupation.push(value.listValue);
    });

    return (
      (financials.occupation && allOccupation.indexOf(financials.occupation) === -1) ||
      financials.occupation === 'Other'
    );
  }

  getApplicationOwner(): Party {
    if (!this.isOwnerSameAsInsured()) {
      return this.owner;
    }
    return this.insured.person;
  }

  getChannelCode(): string {
    if (this.getProductInfo()) {
      return this.getProductInfo().channel.code;
    }
    return null;
  }

  getChannelInfo() {
    return this.getProductInfo().channel;
  }

  usesUnderwriting(): boolean {
    return (
      this.getChannelCode() === 'CAS' || this.getChannelCode() === 'MMDirect' || this.getChannelCode() === 'LifeBridge'
    );
  }

  getCustomerLanguage() {
    return this.customer.language;
  }

  getCustomerLanguageCode() {
    return Assumptions.languageCodes[this.customer.language];
  }

  isSpanishSubmission(): boolean {
    return this.submittedLanguage === 'es';
  }

  get sourceAppName() {
    if (this.product) {
      const channel = Product.products[this.product].channel.code;

      return Utils.getAppName(channel);
    }

    return null;
  }

  /** Hit-Decounpling: Unused function */
  getTotalIncome() {
    const annualIncome = Utils.access(this, 'insured.financials.annualIncome', 0);
    const otherIncome = Utils.access(this, 'insured.financials.otherIncome', 0);

    return annualIncome + otherIncome;
  }

  /** Hit-Decounpling: Unused function */
  incomeReplRuleFailedForOwner() {
    return this.incomeReplRuleFailed(this.owner);
  }

  incomeReplRuleFailed(entity?: Party | Applicant) {
    const entity_template = entity ? entity : this.insured;
    const entity_age = entity && entity instanceof Person ? entity.age : this.insured.person.age;
    const existingPolicies = entity_template.existingPolicies.existingPolicyList;

    const face =
      this.faceAmount +
      _.filter(existingPolicies, function (existingPolicy) {
        return existingPolicy.status === 'Inforce';
      }).reduce(function (sum, pol) {
        return sum + pol.face;
      }, 0);

    const earnedIncome = entity_template.financials.annualIncome;

    if (earnedIncome) {
      const ratio = face / earnedIncome;

      if (entity_age <= 35) {
        // Must be 30 or lower
        return ratio > 30;
      } else if (entity_age <= 45) {
        return ratio > 25;
      } else if (entity_age <= 55) {
        return ratio > 20;
      } else if (entity_age <= 60) {
        return ratio > 15;
      } else if (entity_age <= 69) {
        return ratio > 10;
      } else {
        return ratio > 5;
      }
    }

    return earnedIncome === 0;
  }

  /** Hit-Decounpling: Unused function */
  canReinstate() {
    return (
      this.workflowState === 'Terminated' &&
      this.workflowSubState === 'Lapsed' &&
      this.canBeReinstatedUntil &&
      this.canBeReinstatedUntil.getTime() > new Date().getTime()
    );
  }

  /** Hit-Decounpling: Unused function */
  onPolicyDocsIssued() {
    const ownerState = this.getApplicationState();
    this.mustMailPaperPacket = <boolean>ApplicationStateLogic.mustMailPaperPacket(ownerState);
    this._setFreeLookEndsOn();
  }

  _setFreeLookEndsOn() {
    this.freeLookPeriodDays = <number>ApplicationStateLogic.freeLookPeriod(this);
    this.freeLookEndsOn = Utils.offsetDate(this.effectiveDate, this.freeLookPeriodDays, 'days');
  }

  //This method is called during AI2/ES2 as an activity and must still set the signed date in the case of backdating
  onApplicationSignedAU(signedDate, signedState, effectiveDate, premBd: BaseWaiverFlatExtras) {
    this.applicationSignedDate = signedDate;
    this.applicationSignedState = signedState;

    this.effectiveDate = this.effectiveDateWithPendingAmendment();
    const effectiveDatePCH = this.getPendingAmendments().find(
      (pch) =>
        pch.status === POLICY_CHANGE_HISTORY_STATUS.PENDING && pch.property === PolicyAmendmentProperty.EFFECTIVE_DATE
    );

    if (effectiveDatePCH) {
      //For Fluildless/AU policies amendments are applied after docusign
      //So for unbackdated policies we do not want to overwrite effectiveDate
      effectiveDatePCH.status = POLICY_CHANGE_HISTORY_STATUS.COMPLETE;
      HitAppCtx.get.exnCtx.dbTxn.setDirty(effectiveDatePCH);
    }

    //If OPs has backdated, effective date will not be null -- in that case do not update the effective date
    if (!this.effectiveDate) {
      const tzdt = this.calculateEffDate(effectiveDate);
      this._setIssueProperties(tzdt, premBd);
    }

    this.aUEffectiveDate = new Date(this.effectiveDate.getTime());
    this.paramedRequiredBy = Utils.offsetDate(signedDate, Assumptions.MUWParamedDays, 'days');
    this.finalDecisionRequiredBy = null;
  }

  onApplicationSignedTLIC(signedDate, signedState) {
    this.applicationSignedDate = signedDate;
    this.applicationSignedState = signedState;
    this.tLICEffectiveDate = signedDate;
    this.paramedRequiredBy = Utils.offsetDate(signedDate, Assumptions.TLICParamedDays, 'days');
    this.finalDecisionRequiredBy = Utils.offsetDate(signedDate, Assumptions.ranOutOfDaysForUWWithTLIC, 'days');
  }

  tlicAccepted() {
    return this.tLICEffectiveDate !== null;
  }

  policyAccepted() {
    return this.freeLookEndsOn !== null;
  }

  policyIsInEffect() {
    const workflowState = this.workflowState;
    return this.freeLookEndsOn !== null && !!workflowState && !workflowState.match(/canceled|terminated/i);
  }

  /** Hit-Decounpling: Unused function */
  getEffectiveDateAUWhenSigned(signedDate) {
    let effDate = new Date(signedDate);
    const insAgeDate = this.insured.getInsuranceAgeDate(this.offeredDate);
    const policyEffectiveDate = this.isSaveAge
      ? this.toGmtFromOwnerTimezone(Utils.offsetDate(insAgeDate, -1, 'day'))
      : this.offerRequiredBy;

    if (policyEffectiveDate < effDate) {
      effDate = new Date(policyEffectiveDate.getTime());
    }

    return effDate;
  }

  onApplicationSignedMUW(signedDate, signedState) {
    this.applicationSignedDate = signedDate;
    this.applicationSignedState = signedState;
    this.paramedRequiredBy = Utils.offsetDate(signedDate, Assumptions.MUWParamedDays, 'days');
    this.finalDecisionRequiredBy = Utils.offsetDate(signedDate, Assumptions.ranOutOfDaysForUWWithoutTLIC, 'days');
  }

  applicationHasBeenSigned() {
    return this.applicationSignedDate !== null;
  }

  insurabilityStatementHasBeenSigned() {
    return this.insurabilitySignedDate !== null;
  }

  //TODO: Look into refactoring this with the Utils.now and date setting
  calculateNextPremiumDueDate() {
    const today = Utils.now();
    let dueDate = Utils.offsetDate(this.effectiveDate, 1, 'month');

    if (dueDate < today) {
      dueDate = Utils.now();
      dueDate.setDate(this.effectiveDate.getDate());

      if (dueDate < today) {
        dueDate = Utils.offsetDate(dueDate, 1, 'month');
      }
    }

    return dueDate;
  }

  getOwnerTimezoneOffsetMins() {
    return Utils.tzDate(Utils.now(), this.getOwnerTimezone()).timezoneOffset();
  }

  toGmtFromOwnerTimezone(date) {
    return new Date(date.getTime() - Utils.milliseconds(this.getOwnerTimezoneOffsetMins(), 'mins'));
  }

  getAdvisorTimezoneOffsetMins() {
    return Utils.tzDate(
      Utils.now(),
      this.primaryPolicyAgent.primaryAgent.person.businessAddress.timezone
    ).timezoneOffset();
  }

  onPolicyOffered(offsetOverrideInDays: number) {
    const insAgeDate = this.insured.getInsuranceAgeDate();
    const now = Utils.now();
    const offsetInDays = Utils.exists(offsetOverrideInDays) ? offsetOverrideInDays : Assumptions.autoIssueEffAfterDays;

    this.offeredDate = now;
    this.offerRequiredBy = Utils.offsetDate(now, offsetInDays, 'days');

    this.isSaveAge = insAgeDate >= now && insAgeDate <= this.offerRequiredBy;
  }

  onPolicyAutoIssue(offsetOverrideInDays: number, premBd: BaseWaiverFlatExtras) {
    this.onPolicyOffered(offsetOverrideInDays);
    const tzdt = this.calculateEffDateOnPolicyOffer(
      offsetOverrideInDays,
      this.offeredDate,
      this.offerRequiredBy,
      this.isSaveAge
    );
    this._setIssueProperties(tzdt, premBd);
  }

  calculateEffDateOnPolicyOffer(
    offsetOverrideInDays: number,
    offeredDate?: Date,
    offerRequiredBy?: Date,
    isSaveAge?: boolean
  ) {
    const insAgeDate = this.insured.getInsuranceAgeDate();

    let tempOfferRequiredByDate = offerRequiredBy,
      tempIsSaveAge = isSaveAge;

    if (!offeredDate) {
      const now = Utils.now();
      const offsetInDays = Utils.exists(offsetOverrideInDays)
        ? offsetOverrideInDays
        : Assumptions.autoIssueEffAfterDays;

      tempOfferRequiredByDate = Utils.offsetDate(now, offsetInDays, 'days');
      tempIsSaveAge = insAgeDate >= now && insAgeDate <= tempOfferRequiredByDate;
    }
    return this.calculateEffDate(
      tempIsSaveAge ? this.toGmtFromOwnerTimezone(Utils.offsetDate(insAgeDate, -1, 'day')) : tempOfferRequiredByDate
    );
  }

  /*
   * case 1: owner signed on dates 1-28 , we want to preserve that date as long as it's 28 in UTC too.
   * case 2: owner signed on dates 28,29,30,31, we want date to be 28 in the database(UTC)
   */
  calculateEffDate(dt: Date) {
    let tzdtInOwnerTimeZone = Utils.tzDate(dt, this.getOwnerTimezone());

    if (tzdtInOwnerTimeZone.getDate() > 28) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'Policy',
          function: 'calculateEffDate',
          category: UICategory.request,
          data: {
            policyId: this._id,
            dateInUTC: dt,
            ownerTimeZone: this.getOwnerTimezone(),
            dateInOwnerTimeZone: tzdtInOwnerTimeZone.format('YYYY-MM-DD HH:mm:ss'),
          },
        },
        'Current date is more than 28 when in owner timezone, we will set date to 28'
      );
      tzdtInOwnerTimeZone.setDate(28);
    }

    const tzdtBackInUTC = Utils.tzDate(tzdtInOwnerTimeZone.toDate(), 'UTC');
    if (tzdtBackInUTC.getDate() > 28) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'Policy',
          function: 'calculateEffDate',
          category: UICategory.request,
          data: {
            policyId: this._id,
            ownerTimeZone: this.getOwnerTimezone(),
            dateBackInOwnerTimeZone: tzdtBackInUTC.format('YYYY-MM-DD HH:mm:ss'),
          },
        },
        'Current date is more than 28 when converted to UTC, we will set date to 28 and hour to 23'
      );
      tzdtBackInUTC.setDate(28);
      tzdtBackInUTC.setHour(23);
      tzdtInOwnerTimeZone = Utils.tzDate(tzdtBackInUTC.toDate(), this.getOwnerTimezone());
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'Policy',
        function: 'calculateEffDate',
        category: UICategory.request,
        data: {
          policyId: this._id,
          dateInUTC: dt,
          ownerTimeZone: this.getOwnerTimezone(),
          dateInOwnerTimeZone: tzdtInOwnerTimeZone.format('YYYY-MM-DD HH:mm:ss'),
          dateBackInUTC: tzdtBackInUTC.format('YYYY-MM-DD HH:mm:ss'),
        },
      },
      'Effective Date after manipulation'
    );

    return tzdtInOwnerTimeZone;
  }

  getNumDaysToConfirmPolicy(): number {
    return 15;
  }

  onPolicyApproved(premBd: BaseWaiverFlatExtras) {
    this.onPolicyOffered(this.getNumDaysToConfirmPolicy());

    if (this.isSaveAge) {
      const tzdt = this.calculateEffDateOnPolicyOffer(
        this.getNumDaysToConfirmPolicy(),
        this.offeredDate,
        this.offerRequiredBy,
        this.isSaveAge
      );

      this._setIssueProperties(tzdt, premBd);
    }
  }

  isInsuranceAgeChangingBetween(dt1, dt2) {
    const insAgeDate = this.insured.getInsuranceAgeDate();

    return insAgeDate >= dt1 && insAgeDate <= dt2;
  }

  onPolicyIssueConfirmed(confirmDate, premBd: BaseWaiverFlatExtras) {
    if (!this.effectiveDate || confirmDate < this.effectiveDate) {
      const tzdt = this.calculateEffDate(confirmDate);
      this._setIssueProperties(tzdt, premBd);
    }
  }

  _setEffectiveDate(tzdt, premBd: BaseWaiverFlatExtras) {
    this.effectiveDate = tzdt.toDate();
  }

  // Overridden in CoverpathBasePolicy
  _setIssueProperties(tzdt, premBd: BaseWaiverFlatExtras) {
    this.paymentDay = tzdt.getDate();
    // always set effective date
    this.effectiveDate = tzdt.toDate();
    this._setFreeLookEndsOn();
    this._updatePremiumBreakdown(premBd);
    HitAppCtx.get.exnCtx.dbTxn.setDirty(this);
  }

  @remote({ on: 'server' })
  removeFromAccountCenter() {
    // SecReviewed
    this.showInAccountCenter = false;
  }

  @remote({ on: 'server' })
  async getDeferredResults(): Promise<boolean> {
    try {
      const results = await Policy.getKnex().client.raw(
        `select wc.context from customer c
        join policy p on p."customer_id" = c."_id"
        join workflow w on w."referenceId" = p."application_id"
        join workflow_context wc on w."_id" = wc."workflow_id"
        where p."_id" = :policyId
        order by wc."createdTime" DESC`,
        { policyId: this._id }
      );
      const context = JSON.parse(results.rows[0].context);
      if (context.skipDeferredPage) {
        return context.skipDeferredPage;
      }
      return false;
    } catch (err) {
      LoggerSingleton.getInstance().error(
        {
          module: 'Policy',
          function: 'getDeferredResults',
          category: Category.request,

          error: {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
            isHumanRelated: false,
          },
        },
        `Unable to get Deferred results value for customer:  ${this.customer._id}`
      );
    }
    return false;
  }

  isMoreThanQuoted() {
    return this.tLICPremiumBreakdown.getTotal() < this.premiumBreakdown.getTotal();
  }

  lowerCaseFirstLetter(letters) {
    return letters.charAt(0).toLowerCase() + letters.slice(1);
  }

  insuredTypeTrigger() {
    if (this.insuredType === 'spouse') {
      // Valid owner options - you, spouse
      if (this.ownerType === 'partnerfiance') {
        this.ownerType = null;

        PubSub.publish(PropertyChangedEventNames['customer_application_primaryPolicy_ownerType'], '');
        SubjectManager.publish(PropertyChangedEventNames['customer_application_primaryPolicy_ownerType']);
      }
    } else if (this.insuredType === 'partnerfiance') {
      // Valid owner options - you, partnerfiance
      if (this.ownerType === 'spouse') {
        this.ownerType = null;

        PubSub.publish(PropertyChangedEventNames['customer_application_primaryPolicy_ownerType'], '');
        SubjectManager.publish(PropertyChangedEventNames['customer_application_primaryPolicy_ownerType']);
      }
    }
    PubSub.publish(PropertyChangedEventNames['customer_application_primaryPolicy_insuredType'], '');
    SubjectManager.publish(PropertyChangedEventNames['customer_application_primaryPolicy_insuredType']);

    this.ownerTypeTrigger();
  }

  insuredAndOwnerAndPayerTrigger() {
    if (this.insuredAndOwnerAndPayer) {
      this.premiumPayer = 'insured';
    } else {
      this.premiumPayer = ''; //clear selected options
    }
  }

  /** Hit-Decoupling: What is mappers.html used for ? (TODO: HL-39780) */
  getInsuredPronoun() {
    const insuredType = this.insuredType || 'you';

    const pronouns = {
      you: 'you',
      spouse: 'your spouse',
      partnerfiance: 'your partner',
    };

    return pronouns[insuredType];
  }

  getInsuredPossessivePronoun() {
    const insuredType = this.insuredType || 'you';

    const pronouns = {
      you: 'your',
      spouse: "your spouse's",
      partnerfiance: "your partner's",
    };

    return pronouns[insuredType];
  }

  getInsuredPossessivePronounUC() {
    return Utils.capitalizeFirstLetter(this.getInsuredPossessivePronoun());
  }

  getInsuredGender() {
    if (this.doesPolicyRequireUnisexRates()) {
      return 'unisex';
    } else {
      return this.insured.person.isMale() ? 'male' : 'female';
    }
  }

  getInsuredGenderedPronoun() {
    if (this.insured.person.gender === '1') {
      return 'his';
    }

    if (this.insured.person.gender === '2') {
      return 'her';
    }

    return '';
  }

  getInsuredGenderedPronounUC() {
    return Utils.capitalizeFirstLetter(this.getInsuredGenderedPronoun());
  }

  getInsuredTheirPronoun() {
    const insuredType = this.insuredType || 'you';

    const pronouns = {
      you: 'your',
      spouse: 'their',
      partnerfiance: 'their',
    };

    return pronouns[insuredType];
  }

  getInsuredPossessiveAltPronoun() {
    const insuredType = this.insuredType || 'you';

    const pronouns = {
      you: 'yours',
      spouse: "your spouse's",
      partnerfiance: "your partner's",
    };

    return pronouns[insuredType];
  }

  getAreInsuredPronoun() {
    const insuredType = this.insuredType || 'you';

    const pronouns = {
      you: 'Are you',
      spouse: 'Is your spouse',
      partnerfiance: 'Is your partner',
    };

    return pronouns[insuredType];
  }

  getAreInsuredPronounLC() {
    return this.lowerCaseFirstLetter(this.getAreInsuredPronoun());
  }

  getDoInsuredPronoun() {
    const insuredType = this.insuredType || 'you';

    const pronouns = {
      you: 'Do you',
      spouse: 'Does your spouse',
      partnerfiance: 'Does your partner',
    };

    return pronouns[insuredType];
  }

  getDoInsuredPronounLC() {
    return this.lowerCaseFirstLetter(this.getDoInsuredPronoun());
  }

  getWereInsuredPronoun() {
    const insuredType = this.insuredType || 'you';

    const pronouns = {
      you: 'Were you',
      spouse: 'Was your spouse',
      partnerfiance: 'Was your partner',
    };

    return pronouns[insuredType];
  }

  getWereInsuredPronounLC() {
    return this.lowerCaseFirstLetter(this.getWereInsuredPronoun());
  }

  getHaveInsuredPronoun() {
    const insuredType = this.insuredType || 'you';

    const pronouns = {
      you: 'Have you',
      spouse: 'Has your spouse',
      partnerfiance: 'Has your partner',
    };

    return pronouns[insuredType];
  }

  getHaveInsuredPronounLC() {
    return this.lowerCaseFirstLetter(this.getHaveInsuredPronoun());
  }

  getInsuredPronounAre() {
    const insuredType = this.insuredType || 'you';

    const pronouns = {
      you: 'you are',
      spouse: 'your spouse is',
      partnerfiance: 'your partner is',
    };

    return pronouns[insuredType];
  }

  getYouInsuredPronoun() {
    return this.insuredType === 'you' ? 'you' : this.insured.person.firstName;
  }

  getYourInsuredPronoun() {
    return this.insuredType === 'you' ? 'your' : this.insured.person.firstName + "'s";
  }

  getFamilyHistoryPerson(loopIndex: number) {
    if (
      loopIndex >= 0 &&
      this.insured.familyHistory.familyConditions[loopIndex] &&
      this.insured.familyHistory.familyConditions[loopIndex].member
    ) {
      return this.insured.familyHistory.familyConditions[loopIndex].member.toLowerCase();
    } else {
      return 'mother';
    }
  }

  getStateIssuedIdName() {
    const names = {
      drivers: "US driver's license",
      passport: 'passport',
      stateID: 'state-issued ID',
      other: 'ID',
    };

    return names[this.insured.person.idType] || 'ID';
  }

  reasonForInsuranceShortTrigger() {
    if (this.reasonForInsuranceShort === 'FP') {
      this.reasonForInsurance = null;

      PubSub.publish(PropertyChangedEventNames['customer_application_primaryPolicy_reasonForInsuranceShort'], '');
      SubjectManager.publish(PropertyChangedEventNames['customer_application_primaryPolicy_reasonForInsuranceShort']);
    }
  }

  getReasonForInsurance() {
    return this.reasonForInsuranceShort === 'FP' ? true : this.reasonForInsurance;
  }

  getOtherReason() {
    return this.reasonForInsuranceShort !== 'FP';
  }

  getReasonForInsuranceDescription() {
    if (this.getOtherReason) {
      return this.__descriptions__('reasonForInsurance')[<string>this.getReasonForInsurance()];
    }
  }

  copySpouseInfoToBene(bene) {
    bene.person = this.owner;
    bene.address = this.ownerPersonResidentialAddress;
    bene.phone = this.owner.phone;
  }

  _newBeneficiary(type, beneType, bene?: Beneficiary) {
    const beneficiary = bene || Beneficiary.new(this, type);
    // let ChannelPersonClass;

    //  Creating an Entity if beneficiary type is Trust(7) or Other Entity(34)
    if (beneType === '7' || beneType === '34') {
      // TODO this needs to refactored into a const at the least
      beneficiary.party = Entity.new(this.customer, false, this);
      (<Entity>beneficiary.party).legalAddress = Address.new(this.customer);
      beneficiary.party.phone = Phone.new(this.customer);
      if (beneType === '34') {
        (<Entity>beneficiary.party).type = 'other_entity';
        (<Entity>beneficiary.party).distributionPercentage = 100;
      }
    } else {
      beneficiary.party = Person.new(this.customer);
      beneficiary.party.mailingAddress = Address.new(this.customer);
      beneficiary.party.phone = Phone.new(this.customer);
    }

    return beneficiary;
  }

  primaryBeneficiaryAdd() {
    // If type is Trust under will or estate, bene info is not needed
    if (this.beneficiaryType === '2' || this.beneficiaryType === '6') {
      return;
    }

    const beneficiary = this._newBeneficiary('primaryBeneficiary', this.beneficiaryType);
    this.primaryBeneficiary.push(beneficiary);
    beneficiary.setRatios(this.primaryBeneficiary);
  }

  primaryBeneficiaryRemove(i) {
    this.primaryBeneficiary.splice(i, 1);

    if (this.primaryBeneficiary.length > 0) {
      this.primaryBeneficiary[0].setRatios(this.primaryBeneficiary);
    }

    // eslint-disable-next-line no-undef
    PubSub.publish(PropertyChangedEventNames['customer_application_primaryPolicy_primaryBeneficiary'], '');
    SubjectManager.publish(PropertyChangedEventNames['customer_application_primaryPolicy_primaryBeneficiary']);
  }

  primaryBeneficiaryCanRemove() {
    return this.primaryBeneficiary.length > 1;
  }

  primaryBeneficiaryCanAdd() {
    return this.primaryBeneficiary.length < 5;
  }

  primaryBeneficiaryRemoveAll() {
    this.primaryBeneficiary = [];

    PubSub.publish(PropertyChangedEventNames['customer_application_primaryPolicy_primaryBeneficiary'], '');
    SubjectManager.publish(PropertyChangedEventNames['customer_application_primaryPolicy_primaryBeneficiary']);
  }

  /** Hit-Decoupling: Not used (TODO: HL-39780) */
  beneficiaryTypeValuesNoPronoun() {
    let spouse = 'spouse';

    if (this.insuredType === 'partnerfiance') {
      spouse = 'partner or fiance';
    }

    let insert = '';
    let insertLC = '';

    spouse = this.insured.person.firstName + "'s " + spouse;
    insert = insertLC = this.insured.person.firstName + "'s";

    const ret = {};

    ret['9'] = spouse === ' ' ? 'Spouse or partner/fiance' : spouse;
    ret['13'] = insert + ' children';
    ret['1'] = 'Other named individuals';
    ret['2'] = insert + ' estate';
    ret['6'] = 'A trust under ' + insertLC + ' will';

    if (this.isOwnerSameAsInsured()) {
      ret['7'] = 'Other trust';
    }

    return ret;
  }

  isThirdPartyPayer(): boolean {
    return this.isOwnerAlsoPayer === 'false' && this.premiumPayer !== 'insured';
  }

  isNotThirdPartyPayer(): boolean {
    return !this.isThirdPartyPayer();
  }

  isNonCitizenSpecialHandling(): boolean {
    return (
      !this.insured.person.isCitizen() ||
      !this.owner.getIsUSCitizenOrEntity() ||
      (this.contingentOwnerPerson && !this.contingentOwnerPerson.isCitizen())
    );
  }

  beneficiaryTypeTrigger() {
    // Avoid running triggers during final validation
    if (this.isValidating) {
      return;
    }

    this.primaryBeneficiaryRemoveAll();

    // No primary benes if estate or trust under will
    //tslint:disable-next-line
    this.beneficiaryType === '2' || this.beneficiaryType === '6' ? '' : this.primaryBeneficiaryAdd();

    if (!this.is2020CoverpathOrMMD()) {
      // No contingents if type is My Estate, Trust under will, Other Trust, or Other Entity
      if (
        this.beneficiaryType === '2' ||
        this.beneficiaryType === '6' ||
        this.beneficiaryType === '7' ||
        this.beneficiaryType === '34'
      ) {
        this.hasContingentBeneficiary = null;
        this.contingentBeneficiaryRemoveAll();

        PubSub.publish(PropertyChangedEventNames['customer_application_primaryPolicy_hasContingentBeneficiary'], '');
        SubjectManager.publish(
          PropertyChangedEventNames['customer_application_primaryPolicy_hasContingentBeneficiary']
        );
      }
    }

    // Child beneficiaries should always be individually named. ('1': 'Named Individual')
    this.beneficiaryChildrenType = this.beneficiaryType === '13' ? '1' : null;
  }

  getBeneficiaryType() {
    if (this.beneficiaryType === '13') {
      return String(this.beneficiaryChildrenEvenSplit) === 'true' ? this.beneficiaryChildrenType : '1';
    }

    return this.beneficiaryType;
  }

  beneficiaryChildrenEvenSplitTrigger() {
    if (this.beneficiaryChildrenEvenSplit) {
      this.primaryBeneficiary[0].setRatios(this.primaryBeneficiary);

      PubSub.publish(PropertyChangedEventNames['customer_application_primaryPolicy_hasContingentBeneficiary'], '');
      SubjectManager.publish(PropertyChangedEventNames['customer_application_primaryPolicy_hasContingentBeneficiary']);
    }
  }

  hasContingentBeneficiaryTrigger() {
    if (this.hasContingentBeneficiary) {
      if (this.contingentBeneficiary.length === 0) {
        this.contingentBeneficiaryAdd();
      }
    } else {
      this.contingentBeneficiaryRemoveAll();
    }

    PubSub.publish(PropertyChangedEventNames['customer_application_primaryPolicy_beneficiaryChildrenEvenSplit'], '');
    SubjectManager.publish(
      PropertyChangedEventNames['customer_application_primaryPolicy_beneficiaryChildrenEvenSplit']
    );
  }

  contingentBeneficiaryTypeTrigger() {
    // Avoid running triggers during final validation
    if (this.isValidating) {
      return;
    }

    // Child beneficiaries should always be individually named. ('1': 'Named Individual')
    this.contingentBeneficiaryChildrenType = this.contingentBeneficiaryType === '13' ? '1' : null;

    if (this.hasContingentBeneficiary) {
      this.contingentBeneficiaryRemoveAll();
      this.contingentBeneficiaryAdd();

      PubSub.publish(PropertyChangedEventNames['customer_application_primaryPolicy_contingentBeneficiaryType'], '');
      SubjectManager.publish(PropertyChangedEventNames['customer_application_primaryPolicy_contingentBeneficiaryType']);
    }
  }

  getContingentBeneficiaryType() {
    if (this.contingentBeneficiaryType === '13') {
      return String(this.contingentBeneficiaryChildrenEvenSplit) === 'true'
        ? this.contingentBeneficiaryChildrenType
        : '1';
    }

    return this.contingentBeneficiaryType;
  }

  contingentBeneficiaryChildrenEvenSplitTrigger() {
    if (this.contingentBeneficiaryChildrenEvenSplit) {
      this.contingentBeneficiary[0].setRatios(this.contingentBeneficiary);

      PubSub.publish(
        PropertyChangedEventNames['customer_application_primaryPolicy_contingentBeneficiaryChildrenEvenSplit'],
        ''
      );
      SubjectManager.publish(
        PropertyChangedEventNames['customer_application_primaryPolicy_contingentBeneficiaryChildrenEvenSplit'],
        ''
      );
    }
  }

  contingentBeneficiaryAdd() {
    // If type is Trust under will or estate, bene info is not needed
    if (this.contingentBeneficiaryType === '2' || this.contingentBeneficiaryType === '6') {
      return;
    }

    const beneficiary = this._newBeneficiary('contingentBeneficiary', this.contingentBeneficiaryType);

    this.contingentBeneficiary.push(beneficiary);

    beneficiary.setRatios(this.contingentBeneficiary);
  }

  contingentBeneficiaryRemove(beneficiary) {
    const deleteThisBene = typeof beneficiary === 'number' ? this.contingentBeneficiary[beneficiary] : beneficiary;
    for (let ix = 0; ix < this.contingentBeneficiary.length; ++ix) {
      if (this.contingentBeneficiary[ix] === deleteThisBene) {
        this.contingentBeneficiary.splice(ix, 1);
      }
    }

    if (this.contingentBeneficiary.length > 0) {
      this.contingentBeneficiary[0].setRatios(this.contingentBeneficiary);
    }
  }

  contingentBeneficiaryRemoveAll() {
    this.contingentBeneficiary = [];

    PubSub.publish(PropertyChangedEventNames['customer_application_primaryPolicy_contingentBeneficiary'], '');
    SubjectManager.publish(PropertyChangedEventNames['customer_application_primaryPolicy_contingentBeneficiary']);
  }

  contingentBeneficiaryCanRemove() {
    return this.contingentBeneficiary.length > 1;
  }

  contingentBeneficiaryCanAdd() {
    return this.contingentBeneficiary.length < 5;
  }

  isSolePrimaryBeneficiary() {
    // Use main form for spouse or one primary and one continguent beneficiary only
    return this.beneficiaryType === '9' || (this.beneficiaryType === '1' && this.primaryBeneficiary.length === 1);
  }

  isSoleContingentBeneficiary() {
    return (
      !this.contingentBeneficiaryType ||
      this.contingentBeneficiaryType === '9' ||
      (this.contingentBeneficiaryType === '1' && this.contingentBeneficiary.length === 1)
    );
  }

  isSoleBeneficiary() {
    return this.isSolePrimaryBeneficiary() && this.isSoleContingentBeneficiary();
  }

  isNonNamedBeneficiary() {
    return this.beneficiaryType === '2' || this.beneficiaryType === '6';
  }

  isNonNamedContingentBeneficiary() {
    // If is estate or non-named trust
    return this.contingentBeneficiaryType === '2' || this.contingentBeneficiaryType === '6';
  }

  hasMinorBeneficiaries(): boolean {
    return this.primaryBeneficiary.some((beneficiary) => {
      return beneficiary.isMinor || (beneficiary.person && beneficiary.person.dob && beneficiary.person.getAge() < 18);
    });
  }

  /**
   * @returns {Boolean} true if paymentFrequency is annual else false
   */
  isPolicyPaymentFrequencyAnnual(): boolean {
    return CommonUtils.makePaymentFrequencySafeForPolicy(this.paymentFrequency) === 'annual';
  }

  isPolicyPaymentFrequencyMonthly(): boolean {
    return this.paymentFrequency === 'monthly';
  }

  // Overriden in CoverpathBasePolicy
  paymentFrequencyTrigger() {}

  // Overriden in CoverpathPolicy
  isAutoPayTrigger() {}

  getSolePrimaryBeneficiary(): UniversalBeneficiary {
    return this.isSoleBeneficiary()
      ? UniversalBeneficiary.new(this.primaryBeneficiary[0], 'primary')
      : UniversalBeneficiary.new(this._newBeneficiary('primaryBeneficiary', this.beneficiaryType), 'primary');
  }

  getSoleContingentBeneficiary(): UniversalBeneficiary {
    return this.isSoleBeneficiary() || this.contingentBeneficiary.length === 0
      ? UniversalBeneficiary.new(this.contingentBeneficiary[0], 'contingent')
      : UniversalBeneficiary.new(
          this._newBeneficiary('contingentBeneficiary', this.contingentBeneficiaryType),
          'contingent'
        );
  }

  getSolePrimaryBeneRelationshipForDs() {
    if (this.contingentBeneficiary.length > 0) {
      return null;
    }

    return this.getSolePrimaryBeneficiary().getBeneficiaryRelationship();
  }

  countAllBeneficiaries() {
    return this.getAllBeneficiaries().length;
  }

  getAllBeneficiaries(): Array<UniversalBeneficiary> {
    const beneficiaries = [];

    if (this.isSoleBeneficiary()) {
      return beneficiaries;
    }

    if (this.isNonNamedBeneficiary()) {
      // Non-named trust or estate
      beneficiaries.push(UniversalBeneficiary.new(Beneficiary.new(this, 'primaryBeneficiary'), 'primary'));
    }

    const filterBeneficiaries = function () {
      for (let ix = 0; ix < this.primaryBeneficiary.length; ++ix) {
        if (ix === 0 || this.beneficiaryType === '13' || this.beneficiaryType === '1') {
          // Keep out extra garbage
          beneficiaries.push(UniversalBeneficiary.new(this.primaryBeneficiary[ix], 'primary'));
        }
      }
    }.bind(this);

    const filterNonNamedBeneficiaries = function () {
      if (!this.isNonNamedBeneficiary()) {
        if (this.isNonNamedContingentBeneficiary()) {
          beneficiaries.push(UniversalBeneficiary.new(Beneficiary.new(this, 'contingentBeneficiary'), 'contingent'));
        } else {
          for (let ix = 0; ix < this.contingentBeneficiary.length; ++ix) {
            if (ix === 0 || this.contingentBeneficiaryType !== '9') {
              // One spouse only please
              beneficiaries.push(UniversalBeneficiary.new(this.contingentBeneficiary[ix], 'contingent'));
            }
          }
        }
      }
    }.bind(this);

    filterBeneficiaries();
    filterNonNamedBeneficiaries();

    return beneficiaries;
  }

  get servicingAgent() {
    return null;
  }

  utmaNeeded() {
    // If type is children
    if (this.beneficiaryType === '13' || this.contingentBeneficiaryType === '13') {
      return true;
    } else if (
      this.beneficiaryType === '9' ||
      this.beneficiaryType === '1' ||
      this.contingentBeneficiaryType === '9' ||
      this.contingentBeneficiaryType === '1'
    ) {
      // Go thru all the beneficiaries. If atleast one is under 21, return true
      for (let i = 0; i < this.primaryBeneficiary.length; i++) {
        if (
          this.primaryBeneficiary[i].person &&
          this.primaryBeneficiary[i].person.age &&
          this.primaryBeneficiary[i].person.age < 21
        ) {
          return true;
        }
      }

      for (let i = 0; i < this.contingentBeneficiary.length; i++) {
        if (
          this.contingentBeneficiary[i].person &&
          this.contingentBeneficiary[i].person.age &&
          this.contingentBeneficiary[i].person.age < 21
        ) {
          return true;
        }
      }

      return false;
    } else {
      return false;
    }
  }

  /** Hit-Decoupling: Unused function (TODO: HL-39780) */
  getCustodianState() {
    UILoggerSingleton.getInstance().info(
      {
        module: 'Policy',
        function: 'getCustodianState',
        category: UICategory.request,
      },
      'potential-unused-function'
    );

    return this.needCustodian ? this.custodianFirstName + ' ' + this.custodianLastName : '';
  }

  ownerTypeValues() {
    const values = [];

    if (!this.insuredType || this.insuredType === 'you') {
      values.push('you', 'spouse', 'partnerfiance');
    }

    if (this.insuredType === 'spouse') {
      values.push('you', 'spouse');
    }

    if (this.insuredType === 'partnerfiance') {
      values.push('you', 'partnerfiance');
    }

    return values;
  }

  ownerTypeSelectionTrigger() {
    if (this.ownerTypeSelection) {
      this.ownerType = this.ownerTypeSelection;
    } else if (!this.ownerType) {
      this.ownerType = 'you';
    }
    this.ownerTypeTrigger();
    if (this.insuredIsJuvenile() && !this.isOwnerOtherThanSigner()) {
      if (this.ownerType === 'father') {
        this.insured.signer.relationshipToInsured = '12';
      } else if (this.ownerType === 'mother') {
        this.insured.signer.relationshipToInsured = '11';
      } else if (this.ownerType === 'femalelegalguardian') {
        this.insured.signer.relationshipToInsured = '13';
      } else if (this.ownerType === 'malelegalguardian') {
        this.insured.signer.relationshipToInsured = '14';
      }
    }
  }

  ownerTypeTrigger() {
    if (!this.isOwnerSameAsInsured()) {
      if (this.getBeneficiaryType() === '7' && this.isNotLifeBridge()) {
        // Other trust
        this.beneficiaryType = null;
        this.beneficiaryTypeTrigger();

        PubSub.publish(PropertyChangedEventNames['customer_application_primaryPolicy_beneficiaryType'], '');
        SubjectManager.publish(PropertyChangedEventNames['customer_application_primaryPolicy_beneficiaryType']);
      }
      if (this.getContingentBeneficiaryType() === '7') {
        // Other trust
        this.contingentBeneficiaryType = null;
        this.contingentBeneficiaryTypeTrigger();

        PubSub.publish(PropertyChangedEventNames['customer_application_primaryPolicy_contingentBeneficiaryType'], '');
        SubjectManager.publish(
          PropertyChangedEventNames['customer_application_primaryPolicy_contingentBeneficiaryType']
        );
      }
    }
    PubSub.publish(PropertyChangedEventNames['customer_application_primaryPolicy_ownerType'], '');
    SubjectManager.publish(PropertyChangedEventNames['customer_application_primaryPolicy_ownerType']);
  }

  getOwnerRelationshipDocusign() {
    if (this.ownerType === 'spouse' || this.insuredType === 'spouse') {
      return 'spouse';
    } else if (this.ownerType === 'partnerfiance' || this.insuredType === 'partnerfiance') {
      return 'partner';
    }
  }

  isOwnerSameAsInsured(): boolean {
    return this.ownerType === this.insuredType;
  }

  isOwnerSameAsInsuredEs(): boolean {
    return this.isSpanishSubmission() && this.isOwnerSameAsInsured();
  }

  isInsuredInfoSameAsOwners(): boolean {
    return (
      this.insuredType === 'you' &&
      (!this.ownerType ||
        (this.ownerType === 'you' && !this.ownerClassification) ||
        (this.ownerType === 'you' && this.ownerClassification === 'you') ||
        (this.ownerType === 'you' && this.ownerClassification === 'joint'))
    );
  }

  isSelfInsuredAndSelfOwner(): boolean {
    return this.isOwnerSameAsInsured() && this.ownerType === 'you';
  }

  isOwnerOtherThanInsured(): boolean {
    return !this.isOwnerSameAsInsured();
  }

  isOwnerOtherThanInsuredEs(): boolean {
    return this.isSpanishSubmission() && this.isOwnerOtherThanInsured();
  }

  getInsuredOrRepresentative(): string {
    return this.insured.person.getFullName();
  }

  /** Hit-Decoupling: Unused function (TODO: HL-39780) */
  insuredNotSpouseOwnerNotSpouse(): boolean {
    return (
      this.insuredType !== 'spouse' &&
      this.insuredType !== 'partnerfiance' &&
      this.ownerType !== 'spouse' &&
      this.ownerType !== 'partnerfiance'
    );
  }

  /**
   * Still used in older versions of the CP/MMD metadata (< 86)
   */
  /** Hit-Decoupling: Unused function (TODO: HL-39780) */
  insuredIsSpouseAndOwnerNotYou(): boolean {
    return (this.insuredType === 'spouse' || this.insuredType === 'partnerfiance') && this.ownerType !== 'you';
  }

  /** Hit-Decoupling: Unused function (TODO: HL-39780) */
  insuredIsSpouseAndOwnerIsNot(): boolean {
    return (
      (this.insuredType === 'spouse' || this.insuredType === 'partnerfiance') &&
      !['you', 'spouse', 'partnerfiance'].includes(this.ownerType)
    );
  }

  /** Hit-Decoupling: Unused function (TODO: HL-39780) */
  getMaxAgeForJuvenile(): number {
    return this.ownerPersonResidentialAddress.state === Assumptions.stateValues.WA ? 16.5 : 18;
  }

  /**
   * for purposes of metadata display
   */

  isNotLifeBridge() {
    return !this.isLifeBridge();
  }

  isLifeBridge() {
    return this.product === Product.products.ET10.code;
  }

  getApplicationPacketName(): DocTypes {
    return DocTypes.appPacket;
  }

  isITM() {
    return this.application.isITM && this.isAccelerated();
  }

  isAccelerated() {
    // Account for cases where policy is manually moved from Accelerated to TLIC (e.g., upon customer request).
    if (this.isTLIC()) {
      return false;
    }

    return (
      this.acceleratedRateClass > RateClasses.NO_RATING.code &&
      this.acceleratedRateClass < RateClasses.BY_PASS_KNOCKOUT.code &&
      !this.application.isLiteTouch()
    );
  }

  isAcceleratedRateSelect() {
    return this.acceleratedRateClass === RateClasses.SELECT.code;
  }

  isTLIC() {
    return this.tLICRateClass > RateClasses.NO_RATING.code && this.tLICRateClass < RateClasses.KNOCKOUT.code;
  }

  isKnockOut() {
    return !this.isAccelerated() && !this.isTLIC();
  }

  isNotKnockOut() {
    return !this.isKnockOut();
  }

  isLiteTouchApproved() {
    return this.liteTouchDecision === LiteTouchDecision.OFFER;
  }

  isLiteTouchDeclined() {
    return this.liteTouchDecision === LiteTouchDecision.DECLINE;
  }

  isLiteTouchRoutedToLabs() {
    return this.liteTouchDecision === LiteTouchDecision.NEEDS_LABS;
  }

  isOwnerSameAsInsuredAndTLIC() {
    return this.isOwnerSameAsInsured() && this.isTLIC();
  }

  isOwnerSameAsInsuredAndTLICEs() {
    return this.isSpanishSubmission() && this.isOwnerSameAsInsuredAndTLIC();
  }

  isOwnerOtherThanInsuredAndTLIC() {
    return this.isOwnerOtherThanInsured() && this.isTLIC();
  }

  isOwnerOtherThanInsuredAndTLICEs() {
    return this.isSpanishSubmission() && this.isOwnerOtherThanInsuredAndTLIC();
  }

  isCoverpathPolicy(): boolean {
    return this.constructor.name === 'CoverpathPolicy';
  }

  isCoverpathBasePolicy(): boolean {
    return Utils.extendsClass(this, 'CoverpathBasePolicy');
  }

  getHouseholdPhrase() {
    const insuredIsEmployed = this.insured.financials.employed;

    if (insuredIsEmployed || insuredIsEmployed === null) {
      return '';
    }

    return 'household';
  }

  getHouseholdPhraseUC() {
    return Utils.capitalizeFirstLetter(this.getHouseholdPhrase());
  }

  getOwnerPossessivePronoun() {
    const insuredType = this.ownerType || 'you';

    const pronouns = {
      you: 'your',
      spouse: "your spouse's",
      partnerfiance: "your partner's",
    };

    return pronouns[insuredType];
  }

  /** Hit-Decoupling: Unused function */
  getOwnerPossessivePronounUC() {
    return Utils.capitalizeFirstLetter(this.getOwnerPossessivePronoun());
  }

  getOwnerPronoun() {
    const insuredType = this.ownerType || 'you';

    const pronouns = {
      you: 'you',
      spouse: 'your spouse',
      partnerfiance: 'your partner',
    };

    return pronouns[insuredType];
  }

  getOwnerPronounAre() {
    const ownerType = this.ownerType || 'you';

    const pronouns = {
      you: 'you are',
      spouse: 'your spouse is',
      partnerfiance: 'your partner is',
    };

    return pronouns[ownerType];
  }

  getOwnerDiesPhrase() {
    const owner = this.ownerType || 'you';

    const ownerDiesPhrases = {
      you: 'you die',
      spouse: 'your spouse dies',
      partnerfiance: 'your partner dies',
    };

    return ownerDiesPhrases[owner];
  }

  ownerPersonDependsOn() {
    return true;
  }

  hasOwnerPerson() {
    return this.ownerPersonDependsOn();
  }

  get ownerPersonResidentialAddress() {
    return this.owner instanceof Person ? this.owner.residentialAddress : this.owner.legalAddress;
  }

  set ownerPersonResidentialAddress(address) {
    if (this.owner instanceof Person) {
      this.owner.residentialAddress = address;
    } else {
      this.owner.legalAddress = address;
    }
  }

  get ownerPersonVerificationAddress() {
    return this.owner.verificationAddress;
  }

  set ownerPersonVerificationAddress(address) {
    this.owner.verificationAddress = address;
  }

  ownerPersonResidentialAddressDependsOn() {
    return this.ownerPersonDependsOn();
  }

  get ownerPersonMailingAddress() {
    return this.owner.mailingAddress;
  }

  set ownerPersonMailingAddress(address) {
    this.owner.mailingAddress = address;
  }

  ownerPersonMailingAddressDependsOn() {
    return this.ownerPersonDependsOn();
  }

  get ownerPersonPhone() {
    return this.owner.phone;
  }

  set ownerPersonPhone(phone) {
    this.owner.phone = phone;
  }

  ownerPersonPhoneDependsOn() {
    return this.ownerPersonDependsOn();
  }

  ownerEntityDependsOn() {
    return this.ownerType === 'entity';
  }

  getOwnerResidentialAddress(): Address {
    let address = this.insured.address;
    if (this.ownerPersonDependsOn() || this.ownerEntityDependsOn()) {
      address = this.owner instanceof Person ? this.owner.residentialAddress : this.owner.legalAddress;
    }
    return address;
  }

  hasResidentialAddress() {
    return this.ownerType !== 'you';
  }

  getOwnerMailingAddress(): Address {
    return this.ownerPersonDependsOn() || this.ownerEntityDependsOn()
      ? this.owner.mailingAddress
      : this.insured.address;
  }

  hasMailingAddress() {
    return this.ownerType === 'entity';
  }

  ownerRelationshipDependsOn() {
    return this.ownerPersonDependsOn();
  }

  getOwnerAddressOneLinerDocusign() {
    const line1 =
      this.owner instanceof Person ? this.owner.residentialAddress.line1 || '' : this.owner.legalAddress.line1 || '';

    return this.owner instanceof Person
      ? this.owner.residentialAddress + ' ' + line1
      : this.owner.legalAddress + ' ' + line1;
  }

  getOwnerFullName() {
    return this.isOwnerSameAsInsured() ? '' : this.owner.getFullNameOfOwner();
  }

  getOwnerEmailName() {
    return this.owner.getIsEntityOrTrust() ? this.ownerEntity.getFullNameOfOwner() : this.ownerPerson.emailName;
  }

  getOwnerSigningName() {
    return this.owner.getIsEntityOrTrust()
      ? this.ownerEntity.authorizedSigner.fullName
      : this.owner.getFullNameOfOwner();
  }

  getOwnerTaxId() {
    return this.owner.getIsEntityOrTrust() ? this.ownerEntity.taxId : this.ownerPerson[this.ownerPerson.getTaxIdType()];
  }

  getSecondOwnerTaxId() {
    return this.otherOwner[this.otherOwner.getTaxIdType()];
  }

  getOwnerFullNameNA() {
    return this.isOwnerSameAsInsured() ? 'n/a' : this.owner.getFullNameOfOwner();
  }

  getOwnerPhone(): Phone {
    return this.owner.phone;
  }

  getOwnerEmail() {
    return this.owner.email;
  }

  getPayerPronoun() {
    const payerType = this.premiumPayer || 'insured';

    const pronouns = {
      insured: this.getInsuredPronoun(),
      owner: this.getOwnerPronoun(),
    };

    return pronouns[payerType];
  }

  getPayerPossessivePronoun() {
    const payerType = this.premiumPayer || 'insured';

    const pronouns = {
      insured: this.getInsuredPossessivePronoun(),
      owner: this.getOwnerPossessivePronoun(),
    };

    return pronouns[payerType];
  }

  getPayerPossessivePronounUC() {
    return Utils.capitalizeFirstLetter(this.getPayerPossessivePronoun());
  }

  get premiumPayerPerson() {
    return this.payer;
  }

  set premiumPayerPerson(person) {
    this.payer = person;
  }

  get premiumPayerAddress() {
    return this.payer.mailingAddress;
  }

  set premiumPayerAddress(address) {
    this.payer.mailingAddress = address;
  }

  get premiumPayerPhone() {
    return this.payer.phone;
  }

  set premiumPayerPhone(phone) {
    this.payer.phone = phone;
  }

  isInsuredThePayer(): boolean {
    return this.premiumPayer === 'insured';
  }

  getPremiumPayerPerson(): Party {
    return this.premiumPayer === 'insured' ? this.insured.person : this.owner;
  }

  getPremiumPayerPhone(): Phone {
    return this.premiumPayer === 'insured' ? this.insured.phone : this.owner.phone;
  }

  getPremiumPayerAddress(): Address {
    let premiumPayerAddress = this.owner instanceof Person ? this.owner.residentialAddress : this.owner.legalAddress;
    if (this.premiumPayer === 'insured') {
      premiumPayerAddress = this.insured.address;
    }
    return premiumPayerAddress;
  }

  personalNeedsOtherDependsOn(): boolean {
    return this.personalNeeds === 'other';
  }

  businessNeedsOtherDependsOn(): boolean {
    return this.businessNeeds === 'other';
  }

  getPaymentABADigits(): Digit[] {
    return this.getDigits(this.paymentABA);
  }

  getPaymentAccountDigits(): Digit[] {
    return this.getDigits(this.paymentAccount);
  }

  getDigits(num): Digit[] {
    const digits = [];

    while (num.length) {
      digits.push(Digit.new(num.substr(0, 1)));
      num = num.substr(1);
    }

    return digits;
  }

  hasWaiverOfPremiumRider(): boolean {
    return this.getProductTypeInfo().code === 'ROP';
  }

  getExamOneName(): string {
    return Assumptions.examOneDetails.name;
  }

  getExamOneAddress(): string {
    return Assumptions.examOneDetails.address;
  }

  getExamOneCity(): string {
    return Assumptions.examOneDetails.city;
  }

  getExamOneState(): string {
    return Assumptions.examOneDetails.state;
  }

  getExamOneZip(): string {
    return Assumptions.examOneDetails.zip;
  }

  getInsurerName(): string {
    return Assumptions.insurerDetails.name;
  }

  getInsurerAddress(): string {
    return Assumptions.insurerDetails.address;
  }

  getInsurerCity(): string {
    return Assumptions.insurerDetails.city;
  }

  getInsurerState(): string {
    return Assumptions.insurerDetails.state;
  }

  getInsurerZip(): string {
    return Assumptions.insurerDetails.zip;
  }

  setPostIssueApsFlag(flag) {
    this.postIssueApsFlag = flag;
  }

  checkAndSetOnlineParamedScheduledDate(): boolean {
    if (this.onlineParamedScheduledDate < new Date()) {
      this.onlineParamedScheduledDate = null;

      return true;
    }

    return false;
  }

  @remote({ on: 'server' })
  async fetchBaseWaiverFlatExtras(
    rateClass: string,
    effDate?: Date,
    flatExtrasData?: Array<UwFlatExtra>,
    amendedFace?: number,
    amendedTerm?: number,
    rtrFlatExtrasData?: Array<UwFlatExtra>
  ): Promise<BaseWaiverFlatExtras> {
    const rating = RateClasses.find(rateClass);
    const term = amendedTerm || this.term;
    const faceAmount = amendedFace || this.faceAmount;

    if (!rating || !faceAmount) {
      // No rating or no face, no need to hit product service
      return {
        base: 0,
        waiver: 0,
      };
    }

    let premium;

    // `PremiumService` does not support riders
    // For CoverpathPolicy & MMD only use `ProductServiceClient` to get premiums
    if (
      this.amorphic.config.nconf.get('isPremiumServiceEnabled') &&
      !this.isCoverpathPolicy() &&
      !this.isMMDirectPolicy()
    ) {
      let paymentFrequency = ProductApiTypes.PaymentFrequency.Monthly;

      if (/annual/i.test(this.paymentFrequency)) {
        paymentFrequency = ProductApiTypes.PaymentFrequency.Annual;
      }

      // RARE policies may not have term in which case we still want
      // to compute flat extras with a dummy term (20) but ignore base and waiver
      const premiumRequest = {
        context: {
          referenceId: this.policyNumber,
          referenceType: 'PolicyNumber',
        },
        inquiries: [
          {
            product: this.product,
            // https://massmutual.atlassian.net/browse/HL-9757
            // temporary fix until we can fix relationship between product type and product group
            productType: ProductApiTypes.ProductType[this.productType],
            gender: this.insured.person.gender === '1' ? ProductApiTypes.Gender.M : ProductApiTypes.Gender.F,
            paymentFrequency,
            effectiveDate: effDate || Utils.now(),
            includeWaiver: this.waiver,
            rateClassCode: rating.code,
            term: term || 20,
            face: faceAmount,
            state: this.getApplicationState(),
            dob: this.insured.person.dob,
            flatExtras: flatExtrasData || [],
            version: this.productPremiumVersion || null,
            asOfDate: Utils.now(),
          },
        ],
      };

      const result = await PremiumService.getPremiums(premiumRequest);

      premium = result.premiums && result.premiums[0];
    } else {
      // RARE policies may not have term in which case we still want
      // to compute flat extras with a dummy term (20) but ignore base and waiver
      const requestOptions: PremiumRequest = {
        product: this.product,
        // https://massmutual.atlassian.net/browse/HL-9757
        // temporary fix until we can fix relationship between product type and product group
        productType: this.productType,
        gender: this.insured.person.gender,
        ratingCode: rating.code,
        dob: this.insured.person.dob,
        effectiveDate: effDate || Utils.now(),
        state: this.getApplicationState(),
        term: term || 20,
        face: faceAmount,
        flatExtras: flatExtrasData || [],
        waiver: this.waiver,
        paymentFrequency: this.paymentFrequency,
        riders: this.riders
          .map((rider) => ({
            type: rider.type,
            amount: rider.amount,
            accelerationPercent: rider.benefitDisbursementMethod,
            monthlyIncreaseOption: rider.monthlyIncreaseOption,
          }))
          .filter((rider) => {
            if (rider.type === RiderType.ALIR) {
              return false;
            }
            if (
              rider.type === RiderType.CIR &&
              (!Utils.exists(rider.amount) || !Utils.exists(rider.accelerationPercent))
            ) {
              return false;
            }
            return Utils.exists(rider.amount);
          }),
        asOfDate: Utils.now(),
      };

      premium = await ProductServiceClient.getPremium(
        this.policyNumber,
        this.productPremiumVersion || null,
        requestOptions
      );
    }

    let flatExtras: Array<FlatExtra> = null;
    if (flatExtrasData && premium.flatExtras) {
      flatExtras = [];
      flatExtrasData.forEach((feUwObj) => {
        const feResponseObj: FlatExtraResponse = premium.flatExtras.find((fe) => fe.name === feUwObj.ruleCode);
        flatExtras.push(
          FlatExtra.new(
            feUwObj.name,
            feUwObj.amount,
            null,
            null,
            feUwObj.ruleCode,
            feResponseObj.premium,
            feUwObj.expiryAfterInYears
          )
        );
      });
    }

    const rtrRider = premium.riders && premium.riders.find((rider) => rider.type === RiderType.RTR);

    if (rtrRider && rtrRider.flatExtras) {
      rtrRider.flatExtras.forEach((fe) => {
        const found = rtrFlatExtrasData.find((rtrfe) => rtrfe.name === fe.name);
        if (!found) {
          rtrFlatExtrasData.push(
            FlatExtra.new(fe.name, fe.amount, null, null, fe.name, fe.premium, fe.expiryAfterInYears)
          );
        }
      });
    }

    if (rtrFlatExtrasData && premium.riders) {
      premium.riders.forEach((rider) => {
        const riderFlatExtras = [];
        rider.flatExtras.forEach((riderFE) => {
          const fe = rtrFlatExtrasData.find((feData) => feData.ruleCode === riderFE.name);
          if (fe) {
            riderFlatExtras.push(
              FlatExtra.new(fe.name, fe.amount, null, null, fe.ruleCode, riderFE.premium, fe.expiryAfterInYears, null)
            );
          }
        });

        rider.flatExtras = riderFlatExtras;
      });
    }

    if (!term) {
      premium.base = premium.waiver = 0;
    }

    if (!this.productPremiumVersion) {
      this.productPremiumVersion = premium.version;
    }

    const response = {
      base: premium.base,
      waiver: this.waiver ? premium.waiver : 0,
      flatExtras: flatExtras,
      riders: premium.riders || [],
    };

    return response;
  }

  async fetchFlatExtras(
    flatExtrasData: Array<UwFlatExtra>,
    face?: number,
    rtrFlatExtrasData?: Array<UwFlatExtra>
  ): Promise<BaseWaiverFlatExtras> {
    const faceAmount = face || this.faceAmount;

    if (!faceAmount) {
      return null;
    }

    // Use a dummy rate class of SELECT for Lifebridge and ULTRA for all others to get past the
    // schema validation of Product service. The rate class is not used in computing flat extras.
    const rateClass = this.isLifeBridge() ? RateClasses.SELECT.code : RateClasses.ULTRA.code;
    const premBd: BaseWaiverFlatExtras = await this.fetchBaseWaiverFlatExtras(
      rateClass,
      null,
      flatExtrasData,
      face,
      null,
      rtrFlatExtrasData
    );
    return premBd;
  }

  applyTokenUsed() {
    if (this.customer) {
      this.customer.applyTokenUsed();
      HitAppCtx.get.exnCtx.dbTxn.setDirty(this.customer);
    }
    if (this.insured) {
      this.insured.applyTokenUsed();
      HitAppCtx.get.exnCtx.dbTxn.setDirty(this.insured);
    }
  }

  fetchAndCreateClonedPolicy(): Promise<Policy> {
    return Promise.resolve().then(this.createClonedPolicy.bind(this));
  }

  async createClonedPolicy(keepOriginal?: boolean, sourceType?: ApplicationSourceType): Promise<Policy> {
    // For each property we get to decide how to clone it by either returning
    // the target object or returning null which means a mindless clone is to be done
    // Anything linked to customer needs to NOT clone customer but use the existing one
    const CustomerType = this.customer.amorphicClass;
    const AppType = this.application.amorphicClass;
    const PolicyType = this.amorphicClass;
    const ApplicantType = this.insured.amorphicClass;
    const PersonType = this.insured.person.amorphicClass;

    const customerTypeName = CustomerType.amorphicClassName;
    const appTypeName = AppType.amorphicClassName;
    const policyTypeName = PolicyType.amorphicClassName;
    const applicantTypeName = ApplicantType.amorphicClassName;

    const newPolicy = this.createCopy(
      function (obj, prop, template) {
        switch (template.amorphicClassName) {
          case 'Assumptions':
            return Assumptions;

          case 'Customer':
          case customerTypeName:
            return [this.customer];
          case 'Agent':
            return [this.primaryPolicyAgent.agent];

          // Don't traverse
          case 'Application':
          case appTypeName:
            return [this.application];
          // Don't traverse!
          case 'Policy':
          case policyTypeName:
            return null;
          case 'PolicyDoc':
          case 'UwReasons':
          case 'ESignSessionMetadata':
          case 'FollowUp': // we will manually clone those - if any
            return undefined;
        }

        switch (obj.amorphicClass.amorphicClassName + '.' + prop) {
          case applicantTypeName + '.party':
          case policyTypeName + '.insured':
          case policyTypeName + '.person':
          case policyTypeName + '.address':
          case policyTypeName + '.phone':
          case policyTypeName + '.ownerPerson':
          case policyTypeName + '.ownerEntity':
          case policyTypeName + '.premiumPayerPerson':
          case policyTypeName + '.premiumPayerAddress':
          case policyTypeName + '.premiumPayerPhone':
          case policyTypeName + '.doctorAddress':
          case policyTypeName + '.ownerPersonPhone':
          case policyTypeName + '.ownerPersonResidentialAddress':
          case policyTypeName + '.ownerPersonMailingAddress':
          case 'Applicant.party':
          case 'Applicant.address':
          case 'Applicant.phone':
          case 'Policy.insured':
          case 'Policy.person':
          case 'Policy.address':
          case 'Policy.phone':
          case 'Policy.ownerPerson':
          case 'Policy.ownerEntity':
          case 'Policy.premiumPayerPerson':
          case 'Policy.premiumPayerAddress':
          case 'Policy.premiumPayerPhone':
          case 'Policy.doctorAddress':
          case 'Policy.ownerPersonPhone':
          case 'Policy.ownerPersonResidentialAddress':
          case 'Policy.ownerPersonMailingAddress':
          case 'Beneficiary.party':
            return template.new(this.customer);

          case policyTypeName + '.rejectedReasons':
            return undefined;

          case 'CoverpathPolicy.riders':
            return undefined;

          case 'Policy.primaryPolicyAgent':
            return PolicyAgent.new(
              null,
              this.primaryPolicyAgent.agent,
              this.primaryPolicyAgent.ratio,
              this.primaryPolicyAgent.renewalCommissionRatio,
              this.primaryPolicyAgent.agencyId,
              this.primaryPolicyAgent.corpId,
              this.primaryPolicyAgent.corpAgency
            );
        }

        return null;
      }.bind(this)
    );

    newPolicy.maxFace = null;
    newPolicy.convertedFrom = null;
    newPolicy.convertedTo = null;
    newPolicy.product = this.product;

    // Start with insured and owner as different objects as that's how a new policy is setup
    if (newPolicy.isOwnerSameAsInsured()) {
      newPolicy.owner = Person.new(this.customer, true); //true means copy Financials
      newPolicy.ownerPersonResidentialAddress = Address.new(this.customer);
      newPolicy.ownerPersonPhone = Phone.new(this.customer);

      // retain isTINCorrect value
      newPolicy.ownerPerson.isTINCorrect = (<Person>this.owner).isTINCorrect;
    }

    // Clear policy data obtained as part of underwriting
    newPolicy.resetWorkflowProps();

    if (keepOriginal) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'Policy',
          function: 'createClonedPolicy',
          category: UICategory.request,
          data: {
            policy_id: Utils.extractPersistorObjectDbId(this.__id__.toString()),
          },
        },
        'Policy cloned.'
      );
    } else {
      UILoggerSingleton.getInstance().info(
        {
          module: 'Policy',
          function: 'createClonedPolicy',
          category: UICategory.request,
          data: {
            policy_id: Utils.extractPersistorObjectDbId(this.__id__.toString()),
          },
        },
        'Policy retired as application_primaryPolicy'
      );

      // Save the cloned policy and don't show the cancelled policy in the
      // account center anymore. In flight policy (application_primaryPolicy) is now the cloned policy

      this.removeFromAccountCenter();
    }

    // The implementation of this method would skip for Coverpath/MMD policies because they do not use selectedQuote anymore
    if (!this.customer.isCoverpathCustomer() && !this.customer.isMMDirectCustomer()) {
      await updateSelectedQuote(this, newPolicy, this.customer);
    }
    resetArrayIds(newPolicy);
    return newPolicy;

    // TODO: There is a ticket to consolidate all ways we store premium values: B-36607
    // This function should not be used for Coverpath and MMD policies.
    function updateSelectedQuote(oldPolicy: Policy, newPolicy: Policy, customer: Customer): Promise<void> {
      if (oldPolicy.hasHadPolicyChanges() && customer.selectedQuote) {
        const quoteEngine = customer.quoteEngine || Quotes.new();
        const selectedCustomerQuote = customer.selectedQuote.policies[0];
        const selectedPolicyQuote = newPolicy.selectedQuote.policies[0];

        return quoteEngine
          .fetchProductQuoteOnServer(
            oldPolicy.product,
            oldPolicy.evaluateInsuredGender(),
            oldPolicy.evaluateHealthClass(),
            oldPolicy.isSmokerByApp(),
            oldPolicy.term,
            oldPolicy.faceAmount,
            oldPolicy.insured.address.state,
            oldPolicy.insured.person.dob,
            true,
            oldPolicy.policyNumber,
            oldPolicy.finalRateClass || oldPolicy.tLICRateClass
          )
          .then((response) => {
            if (amorphicStatic.config.nconf.get('isPremiumServiceEnabled')) {
              const evaluatedQuote = <PremiumServiceResponse>response;
              const premium = evaluatedQuote.premiums[0];
              selectedCustomerQuote.monthly = premium.base;
              selectedPolicyQuote.monthly = premium.base;
            } else {
              const evaluatedPremium = <PremiumResponse>response;
              selectedCustomerQuote.monthly = evaluatedPremium.base;
              selectedPolicyQuote.monthly = evaluatedPremium.base;
            }

            selectedCustomerQuote.face = oldPolicy.faceAmount;
            selectedCustomerQuote.term = oldPolicy.term;

            selectedPolicyQuote.face = oldPolicy.faceAmount;
            selectedPolicyQuote.term = oldPolicy.term;
          });
      }
    }

    /**
     * TODO: HL-34405 (DOI team)
     * This is a hack so that the array cloning does not reverse the order of the elements
     * Currently applying it for authorizedTrusteeList
     */
    function resetArrayIds(newPolicy: Policy) {
      if (
        newPolicy.ownerEntity &&
        newPolicy.ownerEntity.authorizedTrusteeList &&
        newPolicy.ownerEntity.authorizedTrusteeList.length > 0
      ) {
        newPolicy.ownerEntity.authorizedTrusteeList.forEach((trustee) => {
          trustee._id = null;
          trustee.generateId();
        });
      }
    }
  }

  resetWorkflowProps() {
    // Clear dates
    this.canBeReinstatedUntil =
      this.issuedDate =
      this.willLapseOn =
      this.freeLookEndsOn =
      this.paramedRequiredBy =
      this.paramedScheduledDate =
      this.offerRequiredBy =
      this.offeredDate =
      this.aUEffectiveDate =
      this.tLICEffectiveDate =
      this.effectiveDate =
      this.workflowStateUpdate =
      this.submittedAt =
      this.issuePolicyRequestDate =
        null;
    //clear appMetadataVersion
    this.appMetadataVersion = null;

    // Clear other data
    this.showInAccountCenter = true;
    this.workflowState = this.workflowSubState = this.futureWorkflowState = this.futureWorkflowSubState = '';
    this['workflowPersistor'] = null;
    this.productPremiumVersion = null;

    //clear LT data
    this.liteTouchDecision = null;
    this.isLiteTouch = null;

    this.isSaveAge = false;
    this.rejectedReasons = [];
    this.flatExtras = 0;
    this.applicationSignedDate = this.applicationSignedState = null;
    this.acceleratedRateClass =
      this.tLICRateClass =
      this.tLICAlgoRateClass =
      this.finalUWType =
      this.finalManualRateClass =
      this.finalRateClass =
      this.finalAlgoRateClass =
      this.waiverPremium =
        null;
    this.policyNumber = null;
    this.idCheckAttempts = this.drivingRecordAttempts = 0;
    this.policyDocs = this.policyChanges = this.workflowStateHistory = this.eSignDocMetadata = [];

    // Reset any signatures
    this.policySignedDateOwner =
      this.policySignedDateInsured =
      this.amendmentSignedDateOwner =
      this.amendmentSignedDateInsured =
      this.insurabilitySignedDate =
        null;

    UILoggerSingleton.getInstance().info(
      {
        module: 'Policy',
        function: 'resetWorkflowProps',
        data: {
          policyNumber: this.policyNumber,
        },
        category: UICategory.request,
      },
      'Policy cleaned and cloned'
    );
  }

  @serverOnly()
  async assignPolicyNumber(): Promise<string> {
    this.policyNumber = await ProductServiceClient.getSequence({
      productCode: this.product,
      referenceId: this.application && this.application.applicationNumber,
    });
    return this.policyNumber;
  }

  isSubmitting() {
    return this.workflowState === 'TLIC Application';
  }

  startWorkflow(firstStage) {
    return this.application.startWorkflow(firstStage);
  }

  async fulfillReq(req: string, futureSubState?: string, futureState?: string, forceDependent?: boolean) {
    this.fulfillReqs([req], futureSubState, futureState, forceDependent);
  }

  async fulfillReqs(requirements: string[], futureSubState?: string, futureState?: string, forceDependent?: boolean) {
    if (futureSubState) {
      this.futureWorkflowSubState = futureSubState;
    }

    if (futureState) {
      this.futureWorkflowState = futureState;
    }
    this.customer.registerPostServerCallCallback(() => this.fulfillRequirements(requirements, forceDependent));
  }

  async fulfillRequirements(requirements: string[], forceDependent?: boolean) {
    UILoggerSingleton.getInstance().info(
      {
        module: 'Policy',
        function: 'fulfillRequirements',
        data: {
          'policyNumber': this.policyNumber,
          'requirements': requirements,
          'Future State': this.futureWorkflowState,
          'Future Sub State': this.futureWorkflowSubState,
        },
        category: UICategory.request,
      },
      `fulfillRequirements  publish to Orca`
    );

    //For regression test cases, we will need to set currentTimeOffset in HitAppCtx.newMsgCtx.
    //We didn't setup here because this function is not tested in regression test.
    if (this.convertedFrom) {
      return HitAppCtx.get.startNewExecution(async (exnCtx) => {
        (exnCtx as HitAppExecutionCtx).workflowName = WorkflowNames.AUTOMATED_CONVERSION.name;
        return HitAppCtx.get.policyClient.queueFulfillRequirements(
          this.application._id,
          requirements,
          !!forceDependent
        );
      }, HitAppCtx.newMsgCtx(`policy_${this.policyNumber}`, this.application._id, 'ApplicationId', 'fulfillReq'));
    } else {
      return HitAppCtx.get.startNewExecution(async (exnCtx) => {
        (exnCtx as HitAppExecutionCtx).workflowName = WorkflowNames.getDefaultWorkflow();
        return HitAppCtx.get.policyClient.queueFulfillRequirements(
          this.application._id,
          requirements,
          !!forceDependent
        );
      }, HitAppCtx.newMsgCtx(`policy_${this.policyNumber}`, this.application._id, 'ApplicationId', 'fulfillReq'));
    }
  }

  waiverTrigger() {
    //TODO: renaming 'selectedQuote' variable to 'agentSuggestion' since it is very misleading (HL-5510)
    if (
      this.customer.selectedQuote &&
      this.customer.selectedQuote.agentSuggestion &&
      this.customer.selectedQuote.agentSuggestion.options
    ) {
      const selectedQuote = this.customer.selectedQuote.agentSuggestion;
      const monthlyTotal = this.application.primaryPolicy.waiver
        ? this.application.primaryPolicy.waiverPremium +
          this.application.primaryPolicy.selectedQuote.policies[0].monthly
        : this.application.primaryPolicy.selectedQuote.policies[0].monthly;

      selectedQuote.options.waiverOfPremiumAmount = this.application.primaryPolicy.waiverPremium;
      selectedQuote.options.includeWaiverOfPremium = this.application.primaryPolicy.waiver;

      selectedQuote.costs[0].cost = monthlyTotal;
      this.customer.selectedQuote.costs[0].cost = monthlyTotal;
    }
  }

  // The function tied to metadata that checks if the waiver of premium question should be shown
  // for old MMDirect and for old version of metadata
  getWaiverPremium() {
    let selectedQuote;

    if (this.waiverPremium) {
      return this.waiverPremium;
    }

    if (this.customer.selectedQuote) {
      selectedQuote = this.customer.selectedQuote;
    } else if (this.application && this.application.primaryPolicy && this.application.primaryPolicy.selectedQuote) {
      selectedQuote = this.application.primaryPolicy.selectedQuote;
    } else {
      return; //TODO: should return something (HL-5510)
    }

    const selectedQuotePolicy = selectedQuote.policies[0];

    // Waiver premium applies to HAVEN policies only?
    if (!selectedQuotePolicy) {
      return null;
    }

    // Waiver of premium should be offered only to insureds with an actual age <= 58
    let actualAge = this.insured.getAge();

    // agent can skip the birthdate question, should use agent suggestion if so
    if (selectedQuote.agentSuggestion && actualAge === null) {
      if (!selectedQuote.agentSuggestion.options.includeWaiverOfPremium) {
        this.waiverPremium = selectedQuote.agentSuggestion.options.waiverOfPremiumAmount;
        this.waiver = false;
        return true; //TODO: should not return boolean since other places return number (HL-5510)
      }
      actualAge = selectedQuote.agentSuggestion.options.age;
    }

    if (!actualAge) {
      return null;
    }

    if (actualAge > 58) {
      return null;
    }

    const face = selectedQuotePolicy.face;
    const term = selectedQuotePolicy.term;

    if (face && term) {
      const gender = this.evaluateInsuredGender();
      const smoker = this.isSmokerByWaiverPremiumStandards() ? 'yes' : 'no';
      const healthClass = this.evaluateHealthClass();
      const age = (this.insured.person.dob ? this.insured.getInsuranceAge() : this.insured.person.age) - 18;

      this.waiverPremium = this.fetchWaiverPremium(face, gender, healthClass, smoker, age, term);

      return this.waiverPremium;
    } else {
      return null;
    }
  }

  // deprecated function, we will remove it once the migration is done.
  fetchWaiverPremium(
    face: number,
    gender: string,
    healthClass: string,
    smoker: string,
    age: number,
    term: number
  ): number {
    let premium = null;

    try {
      // if any of the dependencies changed, I neestringTyped to trigger the product service.
      premium = ProductWaiverPremiums[this.product][gender][healthClass][smoker][age][term];
    } catch (err) {
      UILoggerSingleton.getInstance().error(
        {
          module: 'Policy',
          function: 'fetchWaiverPremium',
          category: UICategory.request,
          error: {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
            isHumanRelated: false,
            data: {
              product: this.product,
              gender: gender,
              healthClass: healthClass,
              smoker: smoker,
              age: age,
              term: term,
            },
          },
        },
        'Cant find waiver for premium: '
      );
      premium = 0;
    }

    if (premium) {
      premium = (premium * face) / 1e3;

      return Math.round((premium / 12) * 100) / 100;
    } else {
      return 0;
    }
  }

  setQuote(selectedQuote) {
    const options = selectedQuote.options || (selectedQuote.agentSuggestion && selectedQuote.agentSuggestion.options);

    this.selectedQuote = selectedQuote;
    this.faceAmount = this.selectedQuote.policies[0].face;
    this.faceAmountTrigger();
    this.term = this.selectedQuote.policies[0].term;
    this.carrier = selectedQuote.policies[0].carrierCode;
    this.tLICPremiumBreakdown = createOrUpdate.call(this, this.tLICPremiumBreakdown, TLICPremiumBreakdown);
    this.finalAlgoPremiumBreakdown = createOrUpdate.call(this, this.finalAlgoPremiumBreakdown, PremiumBreakdown);
    this.premiumBreakdown = createOrUpdate.call(this, this.premiumBreakdown, PremiumBreakdown);

    if (this.selectedQuote && this.selectedQuote.dateOfBirth) {
      const selectedQuoteDob = this.selectedQuote.dateOfBirth;
      const dob = typeof selectedQuoteDob === 'string' ? new Date(selectedQuoteDob) : selectedQuoteDob;

      this.insured.person.dob = dob;
    }

    if (options && typeof options.includeWaiverOfPremium !== 'undefined') {
      this.waiver = options.includeWaiverOfPremium;
    }

    function createOrUpdate(pbObject, pbClass) {
      if (pbObject) {
        pbObject.base = this.selectedQuote.policies[0].monthly;
      } else {
        pbObject = pbClass.new(this.selectedQuote.policies[0].monthly, null, null, this);
      }

      return pbObject;
    }
  }

  public termOrMaxTerm(): number {
    UILoggerSingleton.getInstance().info(
      {
        module: 'Policy',
        function: 'termOrMaxTerm',
        category: UICategory.request,
      },
      'potential-unused-function'
    );

    // Call this first to calculate maxFace if necessary.
    const maxTerm = this.getMaxTerm();

    if (this.term) {
      return this.term;
    } else {
      return maxTerm;
    }
  }

  public getMaxTerm(): number {
    if (!Utils.exists(this.maxTerm)) {
      this.maxTerm = this.calculatedMaxTerm();
    }

    return this.maxTerm;
  }

  getCitizenCap(): number {
    const productSpecs = this.getProductInfo().productSpecs;
    return this.insured.person.isCitizen() ? productSpecs.maxFace : productSpecs.maxFaceForNoncitizens || 0;
  }

  getAgeBasedCap(): number {
    const productSpecs = this.getProductInfo().productSpecs;
    return productSpecs.maxFace;
  }

  protected demographicCap(): number {
    const productSpecs = this.getProductInfo().productSpecs;
    const person = this.insured.person;
    const citizenCap = person.isCitizen() ? productSpecs.maxFace : productSpecs.maxFaceForNoncitizens || 0;
    return Math.min(productSpecs.maxFace, citizenCap);
  }

  getMaximumAvailableCoverageAcrossPolicies(currentTotalFaceAmount: number, product: string): number {
    const availableFaceAmount = this.ageCap() - currentTotalFaceAmount || 0;
    if (availableFaceAmount < Product.productSpecs[product].minFace) {
      return 0;
    }
    // is this rounding needed ? is there any increment I need to take care of ?
    return Policy.roundedMaxFace(availableFaceAmount);
  }

  protected ageCap(): number {
    return this.getProductInfo().productSpecs.maxFace;
  }

  getConfirmIssueMaximum(state: string, subState: string): number {
    if (
      (state === 'TLIC Issued' && subState === 'Waiting for Policy Acceptance') ||
      (state === 'Application' && subState === 'Waiting for Issue Acceptance')
    ) {
      // LI1 / FO1 / FU2 or MI1 / MO1 / MU5
      return this.getMaxFaceInCurrentStageBy50KIncrements();
    }

    return this.demographicCap();
  }

  getMaxFaceInStage(state: string, subState: string): number {
    const productSpecs = this.getProductInfo().productSpecs;
    const demographicCap = this.demographicCap();

    if (state === 'Accelerated Application' && subState === 'Waiting for Acceptance') {
      // AI1 / AO1 / AU2
      const itmMax = productSpecs.maxFaceForITM;
      const instantMax = productSpecs.maxFaceForInstant;
      // (This safety check defaults to a value that would be typical in the Haven channel.)
      const acceleratedLimit = (this.isITM() ? itmMax : instantMax) || 1e6;
      return Math.min(this.maxFace, acceleratedLimit, demographicCap);
    } else if (state === 'TLIC Issued' && subState === 'Waiting for Policy Acceptance') {
      // LI1 / FO1 / FU2
      // Algo Gate face amounts must be constrained to meet reinsurance requirements.
      // (This safety check defaults to a value that would be typical in the Haven channel.)
      const algoMax = productSpecs.maxFaceForAlgoGate || 3e6;
      return Math.min(this.maxFace, algoMax, demographicCap);
    } else if (state === 'Haven Application' && subState === 'Waiting for Application Acceptance') {
      // MA1 / TO1 / MA1
      return demographicCap;
    } else if (state === 'Application' && subState === 'Waiting for Issue Acceptance') {
      // MI1 / MO1 / MU5
      let highestFaceOption: number;
      if (this.maxFace > 0 && this.isOwnerSameAsInsured()) {
        highestFaceOption = Math.min(this.maxFace, productSpecs.maxFace);
        if (this.isLiteTouchApproved()) {
          highestFaceOption =
            Math.min(this.maxFace, productSpecs.maxFaceForNoLabs) || Assumptions.defaultMaxFaceForNoLabs;
        }
      } else {
        highestFaceOption = this.faceAmount;
      }
      return Math.min(highestFaceOption, demographicCap);
    }

    // TC1 / TO1 / TC1 or Amendment:
    return Math.min(this.maxFace, demographicCap);
  }

  getMaxFaceInCurrentStage(): number {
    return this.getMaxFaceInStage(this.workflowState, this.workflowSubState);
  }

  public getMaxFaceInCurrentStageBy50KIncrements(): number {
    const maxFace = this.getMaxFaceInCurrentStage();
    const minFaceForProduct = this.getProductInfo().productSpecs.minFace;

    if (maxFace < minFaceForProduct) {
      const errorMessage = `MaxFace is smaller than ${minFaceForProduct}`;
      const error = {
        isHumanRelated: true,
        data: {
          policyId: this._id,
          policyNumber: this.policyNumber,
          workflowState: this.workflowState,
          workflowSubState: this.workflowSubState,
          maxFace: maxFace,
        },
      };
      UILoggerSingleton.getInstance().error(
        {
          module: 'Policy',
          function: 'getMaxFaceInCurrentStageBy50KIncrements',
          category: UICategory.request,
          error: error,
        },
        errorMessage
      );
      throw new Error(errorMessage);
    } else {
      return Policy.roundedMaxFace(maxFace);
    }
  }

  private static roundedMaxFace(unrounded: number): number {
    if (!unrounded) {
      return 0;
    } else {
      const remainder = unrounded % 50000;

      // Faces under $400k only get rounded up when they are within $10k of the next $50k increment.
      const remainderThreshold = unrounded < 400000 ? 40000 : 25000;

      const adjustment = remainder < remainderThreshold ? 0 : 50000;

      return unrounded - remainder + adjustment;
    }
  }

  public calculatedMaxTerm(): number {
    const age = this.insured.getAge();

    UILoggerSingleton.getInstance().info(
      {
        module: 'Policy',
        function: 'calculatedMaxTerm',
        data: {
          age: age,
        },
        category: UICategory.request,
      },
      'Calculating the maximum allowable term length based on age.'
    );

    if (age > this.getProductInfo().productSpecs.maxAgeForHigherTerm) {
      return this.getProductInfo().productSpecs.maxTermForHigherAge;
    } else {
      return this.getProductInfo().productSpecs.restrictedTerm;
    }
  }

  evaluateInsuredGender(): string {
    return this.insured.person.gender === '1' ? 'male' : 'female';
  }

  evaluateHealthClass() {
    let healthClass = this.insured.healthClass.split('S')[0];

    if (healthClass === 'Rg') {
      healthClass = 'R+';
    }

    // If health class is Pf+ and is smoker, drop the class to Pf
    if (healthClass === 'P+' && this.isSmokerByWaiverPremiumStandards()) {
      healthClass = 'Pf';
    }

    return healthClass;
  }

  insuredIsJuvenile(): boolean {
    if (this.insured === null) {
      return false;
    }
    return this.insured.getAge() !== null ? this.insured.getAge() < 18 : false;
  }

  isSmokerByWaiverPremiumStandards(): boolean {
    let isSmoker = false;

    if (this.insured.riskFactors.isSmokester && this.insured.riskFactors.isSmokerYear) {
      // Only a smoker if the user has smoked in the last 12 months
      isSmoker = this.insured.riskFactors.isSmokerYear === 'last12';
    }

    return isSmoker;
  }

  isSmokerByApp(): boolean {
    const riskFactors = this.insured.riskFactors;

    return (
      this.isSmokerByLastSmoked().smoker ||
      this.isSmokerByCigarFrequency().smoker ||
      this.isSmokerByMeds().smoker ||
      this.isSmokerByWhenLastSmoked() ||
      // Note: The unit of the quit...Period properties is the year.
      isLessThanOrEqualTo(riskFactors.quitCigarrettesPeriod, 1) ||
      isLessThanOrEqualTo(riskFactors.quitTobaccoPeriod, 1) ||
      (isLessThanOrEqualTo(riskFactors.quitCigarsPeriod, 1) &&
        isGreaterThanOrEqualTo(riskFactors.usedCigarsFrequency, 3))
    );

    function isLessThanOrEqualTo(a: number | null | undefined, b: number): boolean {
      return Utils.exists(a) && a <= b;
    }

    function isGreaterThanOrEqualTo(a: number | null | undefined, b: number): boolean {
      return Utils.exists(a) && a >= b;
    }
  }

  isSmokerByLastSmoked() {
    return {
      response: this.insured.riskFactors.isSmokerYear,
      smoker: this.insured.smokedInTheLastYear(),
    };
  }

  isSmokerByCigarFrequency() {
    const answer = this.insured.riskFactors.usesCigarsFrequency;

    return {
      response: answer,
      smoker: answer > 2,
    };
  }

  isSmokerByMeds() {
    const answer = this.insured.riskFactors.useTobaccoCessationMeds;

    return {
      response: answer,
      smoker: answer === null ? false : answer,
    };
  }

  isSmokerByWhenLastSmoked() {
    const whenLastSmoked = this.insured.riskFactors.whenLastSmoked;
    const years = this.insured.riskFactors.yearsAgoLastSmoked;
    return (
      whenLastSmoked === 'within_last_year' || (whenLastSmoked === 'years_ago' && (!Utils.exists(years) || years <= 1))
    );
  }

  isSmokerByRiskFactors(): boolean {
    const whenLastUsage = this.insured.riskFactors.whenLastSmokedOrUsedMarijuana;
    const years = this.insured.riskFactors.yearsAgoLastSmokedOrUsedMarijuana;
    return (
      whenLastUsage === 'within_last_year' || (whenLastUsage === 'years_ago' && (!Utils.exists(years) || years <= 1))
    );
  }

  // TODO: If owner starts being different from insured,
  // this function will need to be updated to support it.
  // Take a look at ticket HL-12907.
  isVisaExpired(): boolean {
    const person = this.insured.person;

    if (person.visaExpireDate) {
      return person.visaExpireDate.getTime() < new Date().setUTCHours(0, 0, 0, 0);
    }

    return false;
  }

  setWorkflowState(state, subState, startTime, endTime) {
    if (this.workflowState || this.workflowSubState) {
      this.workflowStateHistory.push(
        WorkflowStateHistory.new(this.workflowState, this.workflowSubState, this.workflowStateUpdate, endTime)
      );
    }

    this.workflowState = state;
    this.futureWorkflowState = '';
    this.workflowSubState = subState;
    this.futureWorkflowSubState = '';
    this.workflowStateUpdate = startTime;
  }

  setCurrentStageRequirements(reqs) {
    this.currentStageRequirements = reqs || [];
  }

  addPolicyDoc(name, doc, type, docSource, appType) {
    this.policyDocs.push(PolicyDoc.new(name, doc, type, docSource, this, appType));
  }

  addPolicyDocFromExisting(policyDoc: PolicyDoc): void {
    policyDoc.policy = this;
    this.policyDocs.push(policyDoc);
  }

  duplicatePolicyDoc(oldPolicyDoc: PolicyDoc): void {
    this.addPolicyDocFromExisting(oldPolicyDoc.clone());
  }

  addPolicyAppPackage(docs) {
    docs.forEach(
      function (doc) {
        this.policyDocs.push(
          PolicyDoc.new(undefined, doc.doc, POLICY_DOC_TYPES.pdf, POLICY_DOC_SOURCES.app, this, doc.typeName)
        );
      }.bind(this)
    );
  }

  addPolicyDocPDF(doc, docSource, appType) {
    const encDoc = Buffer.from(doc).toString('base64');

    this.policyDocs.push(PolicyDoc.new(undefined, encDoc, POLICY_DOC_TYPES.pdf, docSource, this, appType));
  }

  getAdditionalSupplementDetails() {
    //returns array of paragraph strings
    const details = supplementDetails;

    return _.compact(
      _.flattenDeep(
        details.map(
          function (line) {
            const entity = line.entity.reduce(function (obj, ent) {
              return obj[ent];
            }, this.insured);

            return Utils.array(entity).map(function (ent) {
              const answer = line.props.reduce(function (a, p) {
                const ans = ent[p.name];

                return ans === null || (ans === false && p.skipFalse)
                  ? a
                  : a + ' - ' + (p.qText ? p.qText + ' ' : '') + (p.showAnswer ? fmtAnswer(ent, p) : '');
              }, '');

              return answer ? line.qNum + answer : '';
            }, '');
          }.bind(this)
        )
      )
    );

    function fmtAnswer(ent, p) {
      const val = ent[p.name],
        descs = ent[p.name + 'Descriptions'];
      let ans = val;

      if (val === true) {
        ans = 'Yes';
      } else if (val === false) {
        ans = 'No';
      } else if (val instanceof Date) {
        ans =
          ent.amorphicClass && ent.amorphicClass.defineProperties[p.name].rule === 'shortdate'
            ? Utils.dateToMY(val)
            : Utils.dateToMdy(val);
      } else if (descs) {
        ans = descs[val];
      }

      if (p.prefix) {
        ans = p.prefix + ' ' + ans;
      }

      if (p.suffix) {
        ans += ' ' + p.suffix;
      }

      return ans;
    }
  }

  paginateAdditionalSupplementDetails() {
    return pageBreak(this.getAdditionalSupplementDetails(), 85, 25);

    // Assuming 10-pt Courier
    function pageBreak(strArr, lineWidth, pageLines) {
      // Returns array of pages which are strings
      const pages = [];
      let page = '',
        pageLineCnt = 0;

      strArr.forEach(function (str) {
        str.split('\n').forEach(function (strLine) {
          let startPos = 0,
            endPos = 0;

          while (strLine.length - startPos > lineWidth) {
            let sep = '';

            endPos = strLine.lastIndexOf(' ', startPos + lineWidth);

            if (endPos <= startPos) {
              endPos = startPos + lineWidth - 1;
              sep = '-';
            }

            addLine(strLine.substring(startPos, endPos) + sep);
            startPos = endPos;
          }

          addLine(strLine.substring(startPos));
        });

        addLine('');
      });

      addLine('', true);

      return pages;

      function addLine(str, eop?) {
        str = str.trim();

        if (str === '' && page === '') {
          return;
        }

        page = page + str + '\n';
        pageLineCnt++;

        if (pageLineCnt >= pageLines || eop) {
          pages.push(page);
          page = '';
          pageLineCnt = 0;
        }
      }
    }
  }

  countAdditionalSupplementPages() {
    return this.paginateAdditionalSupplementDetails().length;
  }

  countAdditionalSupplementPagesEs() {
    return this.isSpanishSubmission() && this.countAdditionalSupplementPages();
  }

  getAdditionalSupplementPageVarying(pageOffset) {
    return this.paginateAdditionalSupplementDetails()[pageOffset];
  }

  getRunModes() {
    return this.insured.address.line1;
  }

  setRunModes(runModes) {
    this.insured.address.line1 = runModes;
  }

  getOwnerTimezone() {
    return this.owner instanceof Person ? this.owner.residentialAddress.timezone : this.owner.legalAddress.timezone;
  }

  getApplicationHierarchy(): string[] {
    const hierarchy = MetadataHierarchy.build(this.getProductInfo(), this.getApplicationState());

    if (this.wasApplicationStateNonICC()) {
      hierarchy[2] = 'NonICC';
    }

    return hierarchy;
  }

  overrideHierarchyforUW(hierarchy: string[]): string[] {
    if (hierarchy && hierarchy.length > 0) {
      hierarchy[1] = 'Term2';
      hierarchy[2] = 'ICC';
    }
    return hierarchy;
  }

  getHierarchy(): string[] {
    const hierarchy = this.getApplicationHierarchy();
    this.changeProductGroupFromLifeToTerm(hierarchy);
    return hierarchy;
  }

  // Change productGroup from Life to Term to build the correct hierarchy for workflow metadata
  // TODO: The line below should be removed, after productGroup for workflow metadata is changed from Term to Life
  protected changeProductGroupFromLifeToTerm(hierarchy) {
    if (hierarchy[1] === 'LIFE') {
      hierarchy[1] = 'Term';
    }
  }

  // This is a haven specific logic
  wasApplicationStateNonICC() {
    // Certain states might get converted to ICC from NON ICC. Like, CT. This function returns true for them.
    // Returns true for Non ICC policies which has the application state turned to RARE, post application of user.

    const channel = this.getProductInfo().channel.code;
    const origNonICCStates = AvailableStateRegistry[channel].originalNonICCStates;

    return origNonICCStates && origNonICCStates.indexOf(this.getApplicationState()) !== -1 && this.isICCApplication();
  }

  getAppQuestionsCriteria() {
    // default no-op
  }

  isICCApplication() {
    const channel = this.getProductInfo().channel.code;
    const state = this.getApplicationState();
    if (!state) {
      throw new Error(`Policy instantiated with no application state`);
    }
    const isICC = MetadataHierarchy.getConfigurationByChannelAndState(channel, state, 'isICC');

    return isICC;
  }

  doesPolicyRequireUnisexRates() {
    const channel = this.getProductInfo().channel.code;
    const state = this.getApplicationState();

    const hasUnisexRates = MetadataHierarchy.getConfigurationByChannelAndState(channel, state, 'hasUnisexRates');

    return hasUnisexRates;
  }

  isEligibleForAmendment(): boolean {
    return false;
  }

  getInitialPremium(): string {
    let initialPremium;

    // if its not a TLIC, it means knockout/INSTANT TERM, use selectedQuote from quickquote
    if (!this.isTLIC()) {
      initialPremium = this.selectedQuote.policies[0].monthly;
    } else {
      initialPremium = this.tLICPremiumBreakdown.getTotal();
    }

    return Utils.formatNumber(initialPremium, 2);
  }

  getCompletedNewAndOriginalForAttribute(attributeName) {
    function isTargetPolicyChangeComplete(policyChangeHistoryEntry) {
      return (
        policyChangeHistoryEntry.status === POLICY_CHANGE_HISTORY_STATUS.COMPLETE &&
        policyChangeHistoryEntry.property === attributeName
      );
    }

    if (this.policyChanges) {
      const completedChange = _.find(this.policyChanges, isTargetPolicyChangeComplete);

      if (completedChange) {
        return {
          previousValue: completedChange.previousValue,
          newValue: completedChange.newValue,
        };
      }
    }

    return { newValue: '', previousValue: '' };
  }

  getOriginalPolicyValueForAttribute(attributeName) {
    return this.getCompletedNewAndOriginalForAttribute(attributeName).previousValue;
  }

  getCompletedPolicyValueForAttribute(attributeName) {
    return this.getCompletedNewAndOriginalForAttribute(attributeName).newValue;
  }

  hasHadPolicyChanges() {
    if (this.policyChanges) {
      const completedChanges = this.policyChanges.filter(function (policyChange) {
        return policyChange.status === POLICY_CHANGE_HISTORY_STATUS.COMPLETE;
      });

      return completedChanges && completedChanges.length > 0;
    }

    return false;
  }

  wasOfferAmended() {
    const change = _.find(this.policyChanges, function (policyChange) {
      return (
        (policyChange.property === 'faceAmount' ||
          policyChange.property === 'term' ||
          policyChange.property === 'waiver') &&
        policyChange.status === POLICY_CHANGE_HISTORY_STATUS.COMPLETE
      );
    });

    return !!change;
  }

  getPendingAmendments(): Array<PolicyChangeHistory> {
    return this.policyChanges.filter((policyChange) => {
      return policyChange.status === POLICY_CHANGE_HISTORY_STATUS.PENDING;
    });
  }

  // Overriden in CoverpathBasePolicy
  hasPendingAmendment(): boolean {
    return this.application.primaryPolicy.getPendingAmendments().length > 0;
  }

  getPendingUnderwriterAmendment(): Array<PolicyChangeHistory> {
    return this.getPendingAmendments().filter(
      (pendingAmendment) => pendingAmendment.editor === POLICY_CHANGE_EDITOR.AUTO
    );
  }

  // Called from Docusign to stamp out a quote on the pdf for policies (as shown to the user)
  getPolicyPremiumRate(rate: string, isAnnual: boolean): string {
    // If instant term, use final rate otherwise nothing as the policy may or may not have an initial quote.
    let monthly;
    if (this.isAccelerated()) {
      monthly = this.premiumBreakdown.getTotal();
    } else if (this.isTLIC()) {
      monthly = this.tLICPremiumBreakdown.getTotal();
    } else {
      // KO policy. Use the sync quoteEngine here until Docusign's functions are changed to handle
      // async pattern
      // It was a Product decision to use standard or standard tobacco
      const rating = this.isSmokerByApp() ? RateClasses[rate + '_TOBACCO'].code : RateClasses[rate].code;
      const premium = this.getQuoteSync(rating);
      monthly = premium.base + premium.waiver + PremiumBreakdown.getTotalFlatExtras(this.premiumBreakdown.flatExtras);
    }

    monthly = isAnnual ? `$${(monthly * 12).toLocaleString('en')}` : `$${monthly}`;

    return monthly ? monthly : '';
  }

  getQuoteSync(rateClass) {
    const gender = this.getInsuredGender();
    const rating = RateClasses.find(rateClass);
    let base = 0;
    let waiver = 0;

    if (!rating) {
      return {
        base: base,
        waiver: waiver,
      };
    }

    const smoker = rating.smoker;
    const age = this.insured.getInsuranceAge();
    const state = this.insured.address.state;
    const term = this.term;
    const faceAmount = this.faceAmount;
    const quoteEngine = this.customer.quoteEngine || Quotes.new();
    const quote = quoteEngine.fetchQuoteSync(this.product, gender, rating.qCode, smoker, age, term, faceAmount, state);

    if (quote) {
      const smokerStringStatus = rating.smoker ? 'yes' : 'no';

      base = quote.policies[0].monthly;
      waiver = this.waiver
        ? this.fetchWaiverPremium(faceAmount, gender, rating.qCode, smokerStringStatus, age - 18, term)
        : 0;
    }

    return {
      base: base,
      waiver: waiver,
    };
  }

  getPAPolicyPremium() {
    return this.getPolicyPremiumRate(RateClasses.STANDARD.stringCode, this.isWholeLifePolicy());
  }

  getPolicyTitle() {
    return 'Term Life ' + this.term + ' Years';
  }

  getPolicyFace() {
    return '$' + this.getPolicyFaceValue();
  }

  getPolicyFaceValue() {
    return this.faceAmount && this.hasHadPolicyChanges() ? this.faceAmount : this.selectedQuote.policies[0].face;
  }

  getPolicyPremium() {
    return '$' + this.selectedQuote.policies[0].monthly;
  }

  getPolicyTermValue() {
    return this.term && this.hasHadPolicyChanges() ? this.term : this.selectedQuote.policies[0].term;
  }

  getRiderTitle() {
    return 'Accelerated Death Benefit\n' + (this.waiver ? 'Waiver of Premium' : '');
  }

  getWOPTitleForPA() {
    return this.waiver ? 'Waiver of Premium' : '';
  }

  getRiderFaceAmount() {
    return (
      'We will accelerate payment of a portion of the death benefit, upon request, if the Insured is terminally ill.\n' +
      (this.waiver ? 'If the Insured becomes totally disabled, we will waive all premiums.' : '')
    );
  }

  getWOPFaceAmountForPA() {
    return this.waiver ? 'If the Insured becomes totally disabled, we will waive all premiums.' : '';
  }

  getRiderMonthly() {
    return (
      'The cost is included in the premium for the policy.\n\n\n\n' +
      (this.waiver ? Utils.formatCurrency(this.waiverPremium, 2) : '')
    );
  }

  getLatestDocByName(name) {
    return _.reduce(
      this.policyDocs,
      function (latestDoc, doc) {
        const latestDate = (latestDoc || {}).date;

        if (doc.name === name && (!latestDate || doc.date > latestDate)) {
          latestDoc = doc;
        }

        return latestDoc;
      },
      null
    );
  }

  addPolicyChange(policyChangeHistoryObject) {
    this.policyChanges.push(policyChangeHistoryObject);
  }

  setPolicySignedDateByInsured(date: Date) {
    this.policySignedDateInsured = date;
  }

  setPolicySignedDateByOwner(date: Date) {
    this.policySignedDateOwner = date;
  }

  /**
   * Set of functions which update rate classes and
   * corresponding premiumBreakdowns
   */

  setAcceleratedRateClass(rateClass: string) {
    this.acceleratedRateClass = rateClass;
  }

  setTLICRateClass(rateClass: string, premBd: BaseWaiverFlatExtras) {
    this.tLICRateClass = rateClass;
    this._updateTLICPremiumBreakdown(premBd);
    this._updatePremiumBreakdown(premBd);
    this._updateRiders(premBd.riders);
  }

  setFinalAlgoRateClass(finalAlgoRateClass: string, premBd: BaseWaiverFlatExtras, finalRateClass: string) {
    this.finalAlgoRateClass = finalAlgoRateClass;
    this._updateFinalAlgoPremiumBreakdown(premBd);
    this.setFinalRateClass(finalRateClass, premBd, Policy.UW_TYPES.Algo.code);
  }

  setFinalManualRateClass(rateClass: string, premBd: BaseWaiverFlatExtras) {
    this.finalManualRateClass = rateClass;
    this.setFinalRateClass(rateClass, premBd, Policy.UW_TYPES.Manual.code);
  }

  // Gotcha: Note that this and similar functions may require the use of persistor transactions.
  setFinalRateClass(rateClass: string, premBd: BaseWaiverFlatExtras, issueType: string) {
    if (RateClasses.isRated(rateClass) || RateClasses.isTableRated(rateClass) || RateClasses.isDecline(rateClass)) {
      this.finalRateClass = rateClass || this.finalRateClass;
      this.finalUWType = issueType || this.finalUWType;
    } else {
      this.finalRateClass = null;
      this.finalUWType = null;
    }

    this._updatePremiumBreakdown(premBd);
    this._updateRiders(premBd.riders);
  }

  _updateRiders(ridersFromProductService: Rider[]) {
    const policyRiders: Array<PolicyRider> = this.riders;
    if (!ridersFromProductService || !policyRiders) {
      return;
    }

    ridersFromProductService.forEach(({ type, premium, waiver, flatExtras }) => {
      const rider: PolicyRider = policyRiders.find((rider) => rider.type === type);
      rider.update(premium, waiver, flatExtras);
    });
  }

  shouldShowInsuredNonCitizenMessage() {
    const nonCitizenStatuses = ['none', 'residentAlien'];
    if (nonCitizenStatuses.includes(this.insured.person.residencyStatus)) {
      if (this.isOwnerSameAsInsured()) {
        if (this.insuredType === 'you' && this.ownerType === 'you') {
          return false;
        }
      }

      return true;
    }

    return false;
  }

  async calculateTLICPremiumBreakdown(
    tlicRateClass: string,
    flatExtrasData: Array<UwFlatExtra>,
    rtrFlatExtrasData?: Array<UwFlatExtra>
  ): Promise<BaseWaiverFlatExtras> {
    let premBd: BaseWaiverFlatExtras = { base: 0, waiver: 0 };

    if (!!this.faceAmount) {
      if (RateClasses.isRated(tlicRateClass)) {
        premBd = await this.fetchBaseWaiverFlatExtras(
          tlicRateClass,
          this.tLICEffectiveDate,
          flatExtrasData,
          null,
          null,
          rtrFlatExtrasData
        );
      } else {
        // Only fetchFlatExtras so clear out the other premiums
        const premBdFE: BaseWaiverFlatExtras = await this.fetchFlatExtras(flatExtrasData, null, rtrFlatExtrasData);
        if (premBdFE) {
          premBd.flatExtras = premBdFE.flatExtras;
          (premBdFE.riders || []).forEach((riderFE: PolicyRider) => {
            if (!premBd.riders) {
              premBd.riders = [];
            }

            // clearing out other premiums
            riderFE.amount = 0;
            riderFE.premiumBase = 0;
            riderFE.premiumWaiver = 0;
            premBd.riders.push(riderFE);
          });
        }
      }
    }
    return premBd;
  }

  async calculateFinalAlgoPremiumBreakdown(
    finalAlgoRateClass: string,
    flatExtrasData: Array<UwFlatExtra>,
    rtrFlatExtrasData: Array<UwFlatExtra>
  ): Promise<BaseWaiverFlatExtras> {
    let premBd: BaseWaiverFlatExtras = { base: 0, waiver: 0 };

    if (this.finalAlgoPremiumBreakdown && finalAlgoRateClass && RateClasses.isRated(finalAlgoRateClass)) {
      premBd = await this.fetchBaseWaiverFlatExtras(
        finalAlgoRateClass,
        this.effectiveDateWithPendingAmendment(),
        flatExtrasData,
        null,
        null,
        rtrFlatExtrasData
      );
    }
    return premBd;
  }

  async calculatePremiumBreakdown(
    flatExtrasData?: Array<UwFlatExtra>,
    overrideRateClass?: string,
    effectiveDate?: Date,
    rtrFlatExtrasData?: Array<UwFlatExtra>
  ): Promise<BaseWaiverFlatExtras> {
    let premBd: BaseWaiverFlatExtras = { base: 0, waiver: 0 };
    const rateClass = overrideRateClass || this.finalRateClass;
    const flatExtras: Array<UwFlatExtra> = flatExtrasData || Policy.getUWFlatExtras(this.premiumBreakdown.flatExtras);
    const rtrFlatExtras: Array<UwFlatExtra> = rtrFlatExtrasData || Policy.getUWFlatExtras(this.getRtrFlatExtras());
    if (!!this.faceAmount) {
      if (RateClasses.isRated(rateClass) || RateClasses.isTableRated(rateClass)) {
        premBd = await this.fetchBaseWaiverFlatExtras(
          rateClass,
          effectiveDate || this.effectiveDateWithPendingAmendment(),
          flatExtras,
          null,
          null,
          rtrFlatExtras
        );
      } else {
        // Only fetchFlatExtras so clear out the other premiums
        const premBdFE: BaseWaiverFlatExtras = await this.fetchFlatExtras(flatExtras, null, rtrFlatExtrasData);
        if (premBdFE) {
          premBd.flatExtras = premBdFE.flatExtras;
          (premBdFE.riders || []).forEach((riderFE: PolicyRider) => {
            if (!premBd.riders) {
              premBd.riders = [];
            }

            // clearing out other premiums
            riderFE.amount = 0;
            riderFE.premiumBase = 0;
            riderFE.premiumWaiver = 0;
            premBd.riders.push(riderFE);
          });
        }
      }
    }
    return premBd;
  }

  _updateTLICPremiumBreakdown(premBd: BaseWaiverFlatExtras) {
    this.tLICPremiumBreakdown.update(premBd.base, 0, premBd.flatExtras || this.tLICPremiumBreakdown.flatExtras);
  }

  _updatePremiumBreakdown(premBd: BaseWaiverFlatExtras) {
    this.premiumBreakdown.update(premBd.base, premBd.waiver, premBd.flatExtras || this.premiumBreakdown.flatExtras);
  }

  _updateFinalAlgoPremiumBreakdown(premBd: BaseWaiverFlatExtras) {
    this.finalAlgoPremiumBreakdown.update(
      premBd.base,
      premBd.waiver,
      premBd.flatExtras || this.finalAlgoPremiumBreakdown.flatExtras
    );
  }

  static getUWFlatExtras(flatExtras: Array<FlatExtra>): Array<UwFlatExtra> {
    return flatExtras.map((fe) => {
      return {
        ruleCode: fe.ruleCode,
        name: fe.name,
        amount: fe.amount,
        expiryAfterInYears: fe.expiryAfterInYears,
      };
    });
  }

  // Coppied from FormMappingFunctionsMixin
  @property({
    rule: ['currency'],
    validate: 'isWithin(100000, 999999999)',
    comment: 'Death Benefit Amount selected by applicant on application or at time of sale.',
  })
  faceAmount: number | null;

  faceAmountTrigger() {
    // do nothing. be overriden by RAREPolicy
  }

  @property({ toServer: false }) // Can only be modified on the server
  maxFace: number | null = null;

  @property({ toServer: false })
  maxFaceWholeLife: number | null = null;

  @property({ toServer: false })
  maxAnnualPremium: number = null;

  @property({
    comment: "Duration of term policy's level term period",
    compositeValidators: ['policyHasValidTerm', 'policyHasValidFaceAmount'],
  })
  term: number;

  @property({
    comment: 'maximum duration for fixed premium for the policy',
  })
  maxTerm: number | null = null;

  get ownerPerson(): Person {
    return this.owner && this.owner instanceof Person ? <Person>this.owner : null;
  }

  get ownerEntity(): Entity {
    return this.owner && this.owner instanceof Entity ? <Entity>this.owner : null;
  }

  @property({ getType: () => Party })
  owner: Party;

  @property({
    getType: () => Applicant,
    comment:
      'Applicant ID for whom the insurance is being purchased. Use the party details on the ' +
      'Applicant to get the details of the insured',
  })
  insured: Applicant;

  @property({
    logChanges: true,
    comment: 'Date when owner signed the policy contract, CAS only',
  })
  policySignedDateOwner: Date;

  @property({
    logChanges: true,
    comment: 'Date when insured person signed the policy contract, CAS only',
  })
  policySignedDateInsured: Date;

  @property({
    logChanges: true,
    comment: 'If there is amendment, this is the Date time when owner signed',
  })
  amendmentSignedDateOwner: Date;

  @property({
    logChanges: true,
    comment: 'If there is amendment, this is the Date time when insured person signed',
  })
  amendmentSignedDateInsured: Date;

  @property({
    logChanges: true,
    comment: 'If there is the insurability statement, this is the Date time when insured person signed',
  })
  insurabilitySignedDate: Date;

  @property({
    comment: 'Designates the version of App Questions metadata which are held in metadata version and attribute tables',
  })
  appMetadataVersion: number;

  @property({
    comment: 'DEPRECATED: Please use productPremiumVersion instead',
  })
  premiumVersion: number;

  @property({
    comment: 'Designates the version of rates which are held in the product service',
  })
  productPremiumVersion: string;

  //Not used in Coverpath, was added only to keep CP and Haven in sync
  @property({ toClient: false, toServer: false, comment: 'Tracking ID received by the UW Acord API' })
  uwTrackingID: string;

  getPendingPolicyChange(property) {
    return PolicyChangeHistoryManager.getPendingChangeForProperty(this, property);
  }

  getPendingPolicyChangeValue(property) {
    const policyChange = this.getPendingPolicyChange(property);

    return policyChange ? policyChange.newValue : null;
  }

  getAmendmentDetails() {
    const newFace = this.getPendingPolicyChangeValue('faceAmount') || this.faceAmount;
    const newTerm = this.getPendingPolicyChangeValue('term') || this.term;
    return 'Plan is Haven Term ' + newTerm + ' year policy.\n' + 'Face amount is $' + newFace;
  }

  getAmendedFace() {
    return this.getPendingPolicyChangeValue('faceAmount') || this.faceAmount;
  }

  getAmendedTerm() {
    return this.getPendingPolicyChangeValue('term') || this.term;
  }

  getAmendedRate() {
    return this.premiumBreakdown.getTotalWithPendingPremiumAmendment();
  }

  getInsuredFullNameAfterSignature() {
    return this.policySignedDateInsured ? (<Person>this.insured.party).firstNameLastName : '';
  }

  getOwnerFullNameAfterSignature() {
    let nameAfterSigAmendment = '';
    if (!this.owner.getIsEntityOrTrust()) {
      nameAfterSigAmendment = this.ownerPerson.firstNameLastName;
    } else {
      nameAfterSigAmendment = this.ownerEntity.authorizedSigner.firstNameLastName;
    }
    return this.policySignedDateOwner ? nameAfterSigAmendment : '';
  }

  getInsuredFullNameAfterSignature_Amendment() {
    return this.amendmentSignedDateInsured ? (<Person>this.insured.party).firstNameLastName : '';
  }

  getOwnerFullNameAfterSignature_Amendment() {
    let nameAfterSigAmendment = '';
    if (!this.owner.getIsEntityOrTrust()) {
      nameAfterSigAmendment = this.ownerPerson.firstNameLastName;
    } else {
      nameAfterSigAmendment = this.ownerEntity.authorizedSigner.firstNameLastName;
    }
    return this.amendmentSignedDateOwner ? nameAfterSigAmendment : '';
  }
  // End Coppied from FormMappingFunctionsMixin

  getOwnerFullNameAfterSignature_Insurability() {
    let nameAfterSignInsurability = '';
    if (!this.owner.getIsEntityOrTrust()) {
      nameAfterSignInsurability = this.ownerPerson.firstNameLastName;
    } else {
      nameAfterSignInsurability = this.ownerEntity.authorizedSigner.firstNameLastName;
    }
    return this.insurabilitySignedDate ? nameAfterSignInsurability : '';
  }

  get applicationFormName(): string {
    const channel = this.getProductInfo().channel.code;
    const stateConfigs = AvailableStateRegistry[channel][this.getApplicationState()];

    if (stateConfigs) {
      if (stateConfigs.formId) {
        return `A2200HL-${this.getApplicationState()} ${stateConfigs.formId}`;
      }
    }
    return '';
  }

  isInsuredOrOwnerNonCitizen(): boolean {
    const nonCitizenStatuses = ['none', 'residentAlien'];
    let insuredOrOwnerNonCitizen = nonCitizenStatuses.includes(this.insured.person.residencyStatus);
    if (this.insuredType !== this.ownerType) {
      insuredOrOwnerNonCitizen = insuredOrOwnerNonCitizen || !this.owner.getIsUSCitizenOrEntity;
    }
    return insuredOrOwnerNonCitizen;
  }

  isWholeLifePolicy(): boolean {
    return Product.products[this.product].type === Product.productTypes.Whole;
  }

  //Overridden in CoverpathPolicy
  isWholeLifePolicyWithPendingAmendment(): boolean {
    return false;
  }

  isTermLifePolicy(): boolean {
    return Product.products[this.product].type === Product.productTypes.Term;
  }

  //Overridden in CoverpathPolicy
  isTermLifePolicyWithPendingAmendment(): boolean {
    return false;
  }

  isPartOfJointApp(): boolean {
    return this.application.isJointApp();
  }

  isAnnuallyRenewableTerm(): boolean {
    return this.isTermLifePolicyWithPendingAmendment() && this.termWithPendingAmendment() === 1;
  }

  isMMDirectPolicy() {
    return (
      this.product === Product.products.MMDirectTerm.code || this.product === Product.products.MMDirectTermECP.code
    );
  }

  isNotPartOfJointApp(): boolean {
    //while this function is pretty redundant to isPartOfJointApp() if you are in a pure JS/TS environment, these
    //functions are invoked by metadata to create the application questions. Negation of function calls there are
    //much trickier there, so we added this helper function to make the metadata layer easier to work with
    return !this.isPartOfJointApp();
  }

  // Clarification: getTotalFaceAmount() sums across both policies of a joint app, not across policies that are already in force.
  getTotalFaceAmount(): number | null {
    return this.application.getTotalFaceAmount();
  }

  hasTableRating(): boolean {
    return this.finalRateClass && RateClasses.isTableRated(this.finalRateClass);
  }

  isReplacementPolicy(): boolean {
    return (
      this.application.primaryPolicy.insured.existingPolicies.replaceInforceApplication ||
      this.application.primaryPolicy.insured.existingPolicies.existingInForcePolicyListHasReplacement
    );
  }

  shouldSkipReplacement(): boolean {
    return this.application.primaryPolicy.insured.existingPolicies.shouldSkipReplacementOnExistingPolicies(this);
  }

  isInForce(): boolean {
    const regex = /In Force/;
    return regex.test(this.workflowState) || regex.test(this.uwWorkflowState);
  }

  isFreeLook(): boolean {
    const regex = /Free Look/;
    return regex.test(this.workflowState) || regex.test(this.uwWorkflowState);
  }

  isIssued(): boolean {
    return Boolean(this.issuePolicyRequestDate);
  }

  hasEnded(): boolean {
    const regex = /(Canceled)|(Declined)|(Rejected)|(Terminated)/;
    return regex.test(this.workflowState) || regex.test(this.uwWorkflowState);
  }

  isNoReplacementPolicy(): boolean {
    return !this.isReplacementPolicy();
  }

  hasReachedFinalUnderwriting(): boolean {
    return this.workflowStateHistory.some(
      (wf) =>
        (wf.state === 'Manual Underwriting' && wf.subState === 'Final Review') ||
        (wf.state === 'TLIC Issued' && wf.subState === 'Final Underwriting')
    );
  }

  getWorkflowAction() {
    return `${this.workflowState}:${this.workflowSubState}`;
  }

  hasLabOrderBeenSent(): boolean {
    return this.workflowStateHistory.some((wf) => wf.subState === 'Waiting for Labs');
  }

  typeWithPendingAmendment(): string {
    return this.withPendingAmendment(PolicyAmendmentProperty.TYPE) as string;
  }

  /**
   * If the policy has a pending amendment for the specified property, return that.
   * Otherwise, return the value on the policy itself.
   */
  protected withPendingAmendment(prop: PolicyAmendmentProperty): string | number | boolean | Date | null {
    const pendingPCH = this.getPendingPolicyChange(prop);

    if (pendingPCH) {
      return pendingPCH.getTypedNewValue();
    }

    return this[prop];
  }

  /**
   * If the policy has a pending faceAmount amendment, return that.
   * Otherwise, return the value on the policy itself
   */
  faceAmountWithPendingAmendment(): number {
    return this.withPendingAmendment(PolicyAmendmentProperty.FACE_AMOUNT) as number;
  }

  /**
   * If the policy has a pending term length amendment, return that.
   * Otherwise, return the value on the policy itself
   */
  termWithPendingAmendment(): number {
    return this.withPendingAmendment(PolicyAmendmentProperty.TERM) as number;
  }

  /**
   * If the policy has a pending paymentFrequency amendment, return that.
   * Otherwise, return the value on the policy itself
   */
  paymentFrequencyWithPendingAmendment(): string {
    return this.withPendingAmendment(PolicyAmendmentProperty.PAYMENT_FREQUENCY) as string;
  }

  /**
   * If the policy has a pending waiver amendment, return that.
   * Otherwise, return the value on the policy itself
   */
  hasWaiverWithPendingAmendment(): boolean {
    return this.withPendingAmendment(PolicyAmendmentProperty.WAIVER) as boolean;
  }

  getLatestAmendmentDate(): Date {
    if (this.getPendingAmendments().length) {
      let latest;
      this.policyChanges.forEach((change) => {
        if (!latest || change.amendedDate > latest.amendedDate) {
          latest = change;
        }
      });
      return latest.amendedDate;
    }
    return null;
  }

  getCurrentRateClass(getDesc?: boolean): string {
    let rateClassDescription: string = '';
    if (
      this.isAccelerated() ||
      this.finalUWType === Policy.UW_TYPES.Algo.code ||
      this.finalUWType === Policy.UW_TYPES.Manual.code
    ) {
      rateClassDescription = this.finalRateClass;
    } else if (this.isTLIC()) {
      rateClassDescription = this.tLICRateClass;
    } else {
      rateClassDescription = this.isSmokerByApp() ? RateClasses.SELECT_TOBACCO.code : RateClasses.SELECT.code;
    }

    if (getDesc) {
      return RateClasses.find(rateClassDescription).desc;
    }
    return rateClassDescription;
  }

  isInGracePeriod(): boolean {
    return (
      this.status === Assumptions.policyStatus.lapsePending ||
      (!this.policyIsInEffect() && this.tLICStatus === Assumptions.policyStatus.lapsePending)
    );
  }

  isRescindedOrSurrendered(): boolean {
    return this.status === Assumptions.policyStatus.rescinded || this.status === Assumptions.policyStatus.surrendered;
  }

  //overriden in CoverpathBasePolicy
  is2020CoverpathOrMMD(): boolean {
    return false;
  }

  isNot2020CoverpathOrMMD(): boolean {
    return !this.is2020CoverpathOrMMD();
  }

  arePolicyAgentsOnWatchForNWMReplacement(): boolean {
    return false;
  }

  isEcpProduct(): boolean {
    return CommonUtils.isEcpProduct(this.product);
  }

  isInsuredYoungerThanOrEqualToAge(age: number): boolean {
    if (this.insured.person.dob) {
      return CommonUtils.isYoungerThanOrEqualToAge(new Date(this.insured.person.dob), age);
    }
    throw new Error('Insured DOB is undefined');
  }

  isInsuredOlderThanOrEqualToAge(age: number): boolean {
    if (this.insured.person.dob) {
      return CommonUtils.isOlderThanOrEqualToAge(new Date(this.insured.person.dob), age);
    }
    throw new Error('Insured DOB is undefined');
  }

  isValidRTRPolicy() {
    return false;
  }

  isValidGIRPolicy() {
    return false;
  }

  ownerClassificationTrigger() {}

  isAnnuity(): boolean {
    return this.productType === Product.productTypes.Annuity.code;
  }

  isLifeInsurancePolicy(): boolean {
    return Product.productTypes[this.productType].group.name === Product.productGroups.LIFE.name;
  }

  // Overriden in CoverpathPolicy & MMDirectPolicy
  getQuoteHealthClass(): string {
    return '';
  }

  isPolicyAtAmendableWorkflowStage(): boolean {
    const workflowAction = `${this.workflowState}:${this.workflowSubState}`;
    return (
      workflowAction === 'Advisor Amendment:Waiting for Advisor Amendment' ||
      workflowAction === 'Application:Waiting for Issue Acceptance' ||
      workflowAction === 'TLIC Issued:Waiting for Policy Acceptance' ||
      workflowAction === 'Accelerated Application:Waiting for Acceptance'
    );
  }

  isOwnerOtherThanSigner(): boolean {
    return this.insured.hasSigner;
  }

  //Overriden in CoverpathPolicy
  effectiveDateWithPendingAmendment() {
    return this.effectiveDate;
  }

  //Overriden in CoverpathPolicy
  getTotalBasePremiumForRiders() {
    return 0;
  }

  //Overriden in CoverpathPolicy
  productTypeWithPendingAmendment() {
    return this.productType;
  }

  //Overriden in CoverpathPolicy
  ridersWithPendingAmendment() {
    return this.riders;
  }

  //Overriden in CoverpathPolicy
  hasRtrRiderWithPendingAmendment() {
    return false;
  }

  //Overriden in CoverpathPolicy
  hasAlirRiderWithPendingAmendment() {
    return false;
  }

  //Overriden in CoverpathPolicy
  productWithPendingAmendment() {
    return this.product;
  }

  //Overriden in CoverpathPolicy
  getTotalWaiverPremiumForRidersWithPendingAmendment() {
    return 0;
  }

  //Overriden in CoverpathPolicy
  getTotalFlatExtrasForRiders() {
    return 0;
  }

  //Overriden in CoverpathPolicy
  getTotalFlatExtrasForRidersWithPendingAmendment() {
    return 0;
  }

  //Overriden in CoverpathPolicy
  getTotalBasePremiumForRidersWithPendingAmendment() {
    return 0;
  }

  //Overriden in CoverpathPolicy//Includes WOP
  getTotalWaiverPremiumForRiders(premBd?: PremiumBreakdown): number {
    if (!premBd) {
      premBd = this.premiumBreakdown;
    }
    return this.waiver ? premBd.waiver : 0;
  }

  //Overriden in CoverpathPolicy
  isOwnerOtherThanInsuredAndNotJuvenile() {
    return false;
  }

  //Overriden in CoverpathPolicy
  insuredIsJuvenileHasSigner() {
    return false;
  }

  //Overriden in CoverpathBasePolicy
  removeExchange1035() {}

  //Overriden in CoverpathBasePolicy
  set hasAlirRider(value) {}

  //Overriden in CoverpathBasePolicy
  set hasAlirScheduledPayments(value) {}

  //Overriden in CoverpathBasePolicy
  set alirLumpSumUses1035(value) {}

  //Overriden in CoverpathBasePolicy
  get alirRider() {
    return null;
  }

  //Overriden in CoverpathBasePolicy
  get hasAlirLumpSum(): boolean {
    return false;
  }

  //Overriden in CoverpathPolicy
  wholeAPLWithPendingAmendment(): boolean {
    return false;
  }
}

export interface BaseWaiverFlatExtras {
  base: number;
  waiver: number;
  flatExtras?: Array<FlatExtra>;
  riders?: Array<Rider>;
}
